import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
axios.defaults.withCredentials = true;
axios.interceptors.request.use(config => {
  const token = localStorage.getItem('ft_token');
  if (token) config.headers['Authorization'] = 'Bearer ' + token;
  return config;
});

const API = 'https://tires.handytl.com';
const CONDITIONS = ['Good', 'Fair', 'Poor', 'Replace Immediately'];
const BRANDS = ['Michelin', 'Other'];
const SIZES = ['11R22.5', '11R24.5', '445', '385', '295', '285', '275', 'Other'];
const VENDORS = ['Wickel', 'Purcell', 'Les Schwab', 'Other'];
const REPLACEMENT_REASONS = ['Blown', 'Casing', 'Dragged', 'Irregular Wear', 'Mismatch', 'Rock Drill', 'Tread Depth', 'Other'];
const TREAD_PATTERNS = ['XLEZ+', 'XLEZ', 'XLE', 'XL Grip D', 'XDN2', 'XLED+', 'XLED', 'X Multi', 'Other'];
const TREADS = [32,31,30,29,28,27,26,25,24,23,22,21,20,19,18,17,16,15,14,13,12,11,10,9,8,7,6,5,4,3,2,1];

const TRAILER_CONFIGS = {
  "2 Axle Main":  { front: 0, rear: 2 },
  "3 Axle Main":  { front: 0, rear: 3 },
  "4 Axle Main":  { front: 0, rear: 4 },
  "2 Axle Pup":   { front: 1, rear: 1 },
  "3 Axle Pup A": { front: 1, rear: 2 },
  "3 Axle Pup B": { front: 2, rear: 1 },
  "4 Axle Pup":   { front: 2, rear: 2 },
  "5 Axle Pup":   { front: 2, rear: 3 } };

const TRUCK_GRAY = "#4a4a4a";
const TRUCK_LIGHT = "#d0d0d0";
const TRUCK_DARK = "#2a2a2a";
const GLASS = "#a8d4f5";
const TIRE_DEFAULT = "#2a2a2a";
const TIRE_SELECTED = "#e05c2a";
const TIRE_HAS_RECORD = "#1a73e8";
const TIRE_BORDER = "#111";

const getUser = () => { try { return JSON.parse(localStorage.getItem('ft_user')); } catch { return null; } };

function Tire({ x, y, w, h, axle, position, selected, hasRecord, onClick }) {
  const isSelected = Array.isArray(selected)
    ? selected.some(s => s.axle === axle && s.position === position)
    : selected?.axle === axle && selected?.position === position;
  const fill = isSelected ? TIRE_SELECTED : hasRecord ? TIRE_HAS_RECORD : TIRE_DEFAULT;
  return (
    <g onClick={() => onClick(axle, position)} style={{ cursor: "pointer" }}>
      <rect x={x} y={y} width={w} height={h} rx={3} fill={fill} stroke={TIRE_BORDER} strokeWidth={1} />
      {[0.25, 0.5, 0.75].map((t, i) => (
        <line key={i} x1={x + 2} y1={y + h * t} x2={x + w - 2} y2={y + h * t}
          stroke={isSelected ? "#c04010" : hasRecord ? "#1050a0" : "#444"} strokeWidth={0.8} />
      ))}
      <rect x={x + 3} y={y + h * 0.2} width={w - 6} height={h * 0.6} rx={2}
        fill="none" stroke={isSelected ? "#ff9060" : hasRecord ? "#60a0ff" : "#555"} strokeWidth={0.8} />
      <title>Axle {axle} — {position}</title>
    </g>
  );
}

function BoxTrailerIcon({ size = 22 }) {
  const w = size * 2.4, h = size, r = size * 0.13;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      {/* Box body */}
      <rect x={0} y={0} width={w * 0.88} height={h * 0.72} rx={2} fill="currentColor" opacity={0.85} />
      {/* Rear door lines */}
      <line x1={w * 0.44} y1={2} x2={w * 0.44} y2={h * 0.72} stroke="white" strokeWidth={1} opacity={0.4} />
      {/* Undercarriage */}
      <rect x={w * 0.04} y={h * 0.72} width={w * 0.8} height={h * 0.1} rx={1} fill="currentColor" opacity={0.6} />
      {/* Wheels */}
      <circle cx={w * 0.18} cy={h * 0.87} r={r} fill="currentColor" />
      <circle cx={w * 0.32} cy={h * 0.87} r={r} fill="currentColor" />
      <circle cx={w * 0.62} cy={h * 0.87} r={r} fill="currentColor" />
      <circle cx={w * 0.76} cy={h * 0.87} r={r} fill="currentColor" />
      {/* Kingpin connector */}
      <rect x={w * 0.88} y={h * 0.55} width={w * 0.12} height={h * 0.08} rx={1} fill="currentColor" opacity={0.5} />
    </svg>
  );
}

function TruckSVG({ axleCount, driveAxleConfig, selectedTire, onTireClick, records, onChangeAxleConfig }) {
  const hasRecord = (axle, pos) => records?.some(r => r.axle === axle && r.position === pos);
  const W = 300, TW = 22, TH = 44;
  const frameX = 95, frameW = 110, frameCX = frameX + frameW / 2;
  const wideLeft = frameX - 30, wideRight = frameX + frameW + 8;
  const driveLeft = frameX - TW - 3, driveRight = frameX + frameW + 3;
  const dualOuterLeft = frameX - TW * 2 - 6, dualInnerLeft = frameX - TW - 3;
  const dualInnerRight = frameX + frameW + 3, dualOuterRight = frameX + frameW + TW + 6;
  const cabTop = 20, hoodH = 50, cabFaceH = 55, cabBodyH = 40;
  const cabBottom = cabTop + hoodH + cabFaceH + cabBodyH;
  const hoodBottom = cabTop + hoodH, windshieldBottom = hoodBottom + cabFaceH;
  const steerY = cabTop + hoodH * 0.6 - TH / 2;
  const driveGroupTop = cabBottom + 45, driveSpacing = 52;

  const getAxles = () => {
    if (axleCount === 2) return [
      { num: 1, label: "Steer", type: "steer", y: steerY },
      { num: 2, label: "Drive", type: "drive", y: driveGroupTop },
    ];
    if (axleCount === 3) return [
      { num: 1, label: "Steer", type: "steer", y: steerY },
      { num: 2, label: "Drive 1", type: "drive", y: driveGroupTop },
      { num: 3, label: "Drive 2", type: "drive", y: driveGroupTop + driveSpacing },
    ];
    return [
      { num: 1, label: "Steer", type: "steer", y: steerY },
      { num: 2, label: "Pusher", type: "pusher", y: driveGroupTop },
      { num: 3, label: "Drive 1", type: "drive", y: driveGroupTop + driveSpacing },
      { num: 4, label: "Drive 2", type: "drive", y: driveGroupTop + driveSpacing * 2 },
    ];
  };

  const axles = getAxles();
  const lastAxle = axles[axles.length - 1];
  const totalH = lastAxle.y + TH + 40;
  const frameTop = hoodBottom + 10, frameBottom = lastAxle.y + TH + 10;

  const renderAxle = (axle) => {
    const cy = axle.y + TH / 2;
    const isDrive = axle.type === "drive";
    const isDual = isDrive && driveAxleConfig[axle.num] === "dual";
    const isWide = axle.type === "steer" || axle.type === "pusher";
    const leftX = isWide ? wideLeft : (isDual ? dualOuterLeft : driveLeft);
    const rightX = isWide ? wideRight : (isDual ? dualOuterRight : driveRight);
    const configSet = driveAxleConfig[axle.num];
    return (
      <g key={axle.num}>
        <line x1={leftX} y1={cy} x2={rightX + TW} y2={cy} stroke="#777" strokeWidth={4} />
        <circle cx={frameCX} cy={cy} r={6} fill="#999" stroke="#555" strokeWidth={1} />
        <circle cx={frameCX} cy={cy} r={3} fill="#ccc" />
        {isWide ? (
          <>
            <Tire x={wideLeft} y={axle.y} w={TW} h={TH} axle={axle.num} position="Left" selected={selectedTire} hasRecord={hasRecord(axle.num, "Left")} onClick={onTireClick} />
            <Tire x={wideRight} y={axle.y} w={TW} h={TH} axle={axle.num} position="Right" selected={selectedTire} hasRecord={hasRecord(axle.num, "Right")} onClick={onTireClick} />
          </>
        ) : isDual ? (
          <>
            <Tire x={dualOuterLeft} y={axle.y} w={TW} h={TH} axle={axle.num} position="Left Outer" selected={selectedTire} hasRecord={hasRecord(axle.num, "Left Outer")} onClick={onTireClick} />
            <Tire x={dualInnerLeft} y={axle.y} w={TW} h={TH} axle={axle.num} position="Left Inner" selected={selectedTire} hasRecord={hasRecord(axle.num, "Left Inner")} onClick={onTireClick} />
            <Tire x={dualInnerRight} y={axle.y} w={TW} h={TH} axle={axle.num} position="Right Inner" selected={selectedTire} hasRecord={hasRecord(axle.num, "Right Inner")} onClick={onTireClick} />
            <Tire x={dualOuterRight} y={axle.y} w={TW} h={TH} axle={axle.num} position="Right Outer" selected={selectedTire} hasRecord={hasRecord(axle.num, "Right Outer")} onClick={onTireClick} />
          </>
        ) : (
          <>
            <Tire x={driveLeft} y={axle.y} w={TW} h={TH} axle={axle.num} position="Left" selected={selectedTire} hasRecord={hasRecord(axle.num, "Left")} onClick={onTireClick} />
            <Tire x={driveRight} y={axle.y} w={TW} h={TH} axle={axle.num} position="Right" selected={selectedTire} hasRecord={hasRecord(axle.num, "Right")} onClick={onTireClick} />
          </>
        )}
        {isDrive && onChangeAxleConfig && (
          <g onClick={(e) => { e.stopPropagation(); onChangeAxleConfig(axle.num); }} style={{ cursor: 'pointer' }}>
            <rect x={frameCX - 28} y={cy + 8} width={56} height={16} rx={8} fill="#1a73e8" opacity={0.9} />
            <text x={frameCX} y={cy + 19} textAnchor="middle" fontSize={10} fontWeight="700" fill="#fff">
              {configSet ? configSet + ' ✎' : 'set type'}
            </text>
          </g>
        )}
        <text x={W - 4} y={cy + 4} textAnchor="end" fontSize={9} fill="#999">{axle.label}</text>
      </g>
    );
  };

  return (
    <svg width={W} height={totalH} style={{ display: "block", maxWidth: "100%", height: "auto" }}>
      <defs><filter id="shadow"><feDropShadow dx="1" dy="2" stdDeviation="2" floodColor="#00000044" /></filter></defs>
      <rect x={frameX + 10} y={frameTop} width={10} height={frameBottom - frameTop} rx={2} fill={TRUCK_GRAY} />
      <rect x={frameX + frameW - 20} y={frameTop} width={10} height={frameBottom - frameTop} rx={2} fill={TRUCK_GRAY} />
      {axles.filter(a => a.type === "drive").map(a => (
        <rect key={`xm-${a.num}`} x={frameX + 10} y={a.y + TH / 2 - 3} width={frameW - 20} height={6} rx={1} fill={TRUCK_DARK} />
      ))}
      <rect x={frameX - 4} y={cabTop} width={frameW + 8} height={hoodH} rx={6} fill="#3a3a3a" filter="url(#shadow)" />
      <line x1={frameCX} y1={cabTop + 4} x2={frameCX} y2={hoodBottom - 4} stroke="#555" strokeWidth={1.5} />
      {[0.3, 0.55, 0.78].map((t, i) => (
        <rect key={i} x={frameX + 12} y={cabTop + hoodH * t} width={frameW - 24} height={5} rx={2} fill="#555" />
      ))}
      <rect x={frameX - 2} y={cabTop + 6} width={16} height={10} rx={2} fill="#ffffaa" stroke="#cccc44" strokeWidth={1} opacity={0.9} />
      <rect x={frameX + frameW - 14} y={cabTop + 6} width={16} height={10} rx={2} fill="#ffffaa" stroke="#cccc44" strokeWidth={1} opacity={0.9} />
      <rect x={frameX - 8} y={hoodBottom} width={frameW + 16} height={cabFaceH} rx={4} fill={TRUCK_GRAY} filter="url(#shadow)" />
      <rect x={frameX + 4} y={hoodBottom + 6} width={frameW - 8} height={cabFaceH - 16} rx={4} fill={GLASS} opacity={0.85} stroke="#8ab8d8" strokeWidth={1} />
      <rect x={frameX + 8} y={hoodBottom + 9} width={18} height={cabFaceH - 22} rx={3} fill="white" opacity={0.2} />
      <line x1={frameCX} y1={hoodBottom + 6} x2={frameCX} y2={windshieldBottom - 10} stroke="#8ab8d8" strokeWidth={1} />
      <rect x={frameX - 22} y={hoodBottom + 8} width={14} height={8} rx={2} fill={TRUCK_GRAY} stroke={TRUCK_DARK} strokeWidth={1} />
      <rect x={frameX + frameW + 8} y={hoodBottom + 8} width={14} height={8} rx={2} fill={TRUCK_GRAY} stroke={TRUCK_DARK} strokeWidth={1} />
      <rect x={frameX - 8} y={windshieldBottom} width={frameW + 16} height={cabBodyH} rx={4} fill="#383838" />
      <line x1={frameCX} y1={windshieldBottom + 4} x2={frameCX} y2={cabBottom - 4} stroke="#555" strokeWidth={1.5} />
      <rect x={frameX + 10} y={windshieldBottom + 18} width={14} height={4} rx={2} fill={TRUCK_LIGHT} opacity={0.5} />
      <rect x={frameX + frameW - 24} y={windshieldBottom + 18} width={14} height={4} rx={2} fill={TRUCK_LIGHT} opacity={0.5} />
      <rect x={frameX - 10} y={cabBottom - 14} width={10} height={8} rx={2} fill="#555" />
      <rect x={frameX + frameW} y={cabBottom - 14} width={10} height={8} rx={2} fill="#555" />
      {axles.map(axle => renderAxle(axle))}
      <rect x={frameX - 4} y={frameBottom - 4} width={frameW + 8} height={10} rx={3} fill={TRUCK_DARK} />
      <rect x={frameX - 4} y={frameBottom - 16} width={12} height={10} rx={2} fill="#ff4444" opacity={0.85} />
      <rect x={frameX + frameW - 8} y={frameBottom - 16} width={12} height={10} rx={2} fill="#ff4444" opacity={0.85} />
    </svg>
  );
}

function TrailerSVG({ trailerConfig, axleConfig, selectedTire, onTireClick, records, onChangeAxleConfig }) {
  const hasRecord = (axle, pos) => records?.some(r => r.axle === axle && r.position === pos);
  const { front, rear } = TRAILER_CONFIGS[trailerConfig] || { front: 0, rear: 2 };
  const W = 180, TW = 20, TH = 40;
  const trailerX = 50, trailerW = 80, trailerCX = trailerX + trailerW / 2;
  const trailerTop = 20;
  const trailerH = front > 0 ? 340 : 320;
  const trailerBottom = trailerTop + trailerH;
  const axleSpacing = 46;
  const frontGroupTop = trailerTop + 30;
  const rearGroupTop = trailerBottom - 30 - (rear - 1) * axleSpacing - TH;
  const axles = [];
  let axleNum = 1;
  for (let i = 0; i < front; i++) axles.push({ num: axleNum++, y: frontGroupTop + i * axleSpacing, group: 'front' });
  for (let i = 0; i < rear; i++) axles.push({ num: axleNum++, y: rearGroupTop + i * axleSpacing, group: 'rear' });
  const totalH = trailerBottom + 20;

  const renderAxle = (axle) => {
    const cy = axle.y + TH / 2;
    const isDual = axleConfig[axle.num] === 'dual';
    const configSet = axleConfig[axle.num];
    const singleLeftX = trailerX - TW - 6, singleRightX = trailerX + trailerW + 6;
    const dualOuterLeftX = trailerX - TW * 2 - 8, dualInnerLeftX = trailerX - TW - 3;
    const dualInnerRightX = trailerX + trailerW + 3, dualOuterRightX = trailerX + trailerW + TW + 8;
    const axleX1 = isDual ? dualOuterLeftX : singleLeftX;
    const axleX2 = isDual ? dualOuterRightX + TW : singleRightX + TW;
    return (
      <g key={axle.num}>
        <line x1={axleX1} y1={cy} x2={axleX2} y2={cy} stroke="#777" strokeWidth={3.5} />
        <circle cx={trailerCX} cy={cy} r={5} fill="#999" stroke="#555" strokeWidth={1} />
        <circle cx={trailerCX} cy={cy} r={2.5} fill="#ccc" />
        {isDual ? (
          <>
            <Tire x={dualOuterLeftX} y={axle.y} w={TW} h={TH} axle={axle.num} position="Left Outer" selected={selectedTire} hasRecord={hasRecord(axle.num, "Left Outer")} onClick={onTireClick} />
            <Tire x={dualInnerLeftX} y={axle.y} w={TW} h={TH} axle={axle.num} position="Left Inner" selected={selectedTire} hasRecord={hasRecord(axle.num, "Left Inner")} onClick={onTireClick} />
            <Tire x={dualInnerRightX} y={axle.y} w={TW} h={TH} axle={axle.num} position="Right Inner" selected={selectedTire} hasRecord={hasRecord(axle.num, "Right Inner")} onClick={onTireClick} />
            <Tire x={dualOuterRightX} y={axle.y} w={TW} h={TH} axle={axle.num} position="Right Outer" selected={selectedTire} hasRecord={hasRecord(axle.num, "Right Outer")} onClick={onTireClick} />
          </>
        ) : (
          <>
            <Tire x={singleLeftX} y={axle.y} w={TW} h={TH} axle={axle.num} position="Left" selected={selectedTire} hasRecord={hasRecord(axle.num, "Left")} onClick={onTireClick} />
            <Tire x={singleRightX} y={axle.y} w={TW} h={TH} axle={axle.num} position="Right" selected={selectedTire} hasRecord={hasRecord(axle.num, "Right")} onClick={onTireClick} />
          </>
        )}
        {onChangeAxleConfig && (
          <g onClick={(e) => { e.stopPropagation(); onChangeAxleConfig(axle.num); }} style={{ cursor: 'pointer' }}>
            <rect x={trailerCX - 28} y={cy + 4} width={56} height={16} rx={8} fill="#1a73e8" opacity={0.9} />
            <text x={trailerCX} y={cy + 15} textAnchor="middle" fontSize={10} fontWeight="700" fill="#fff">
              {configSet ? configSet + ' ✎' : 'set type'}
            </text>
          </g>
        )}
        <text x={W - 2} y={cy + 4} textAnchor="end" fontSize={8} fill="#999">A{axle.num}</text>
      </g>
    );
  };

  return (
    <svg width={W} height={totalH} style={{ display: "block", maxWidth: "100%", height: "auto" }}>
      <defs><filter id="tshadow"><feDropShadow dx="1" dy="2" stdDeviation="2" floodColor="#00000033" /></filter></defs>
      {front === 0 && (
        <g>
          <rect x={trailerCX - 8} y={trailerTop - 12} width={16} height={16} rx={2} fill="#666" stroke="#444" strokeWidth={1} />
          <circle cx={trailerCX} cy={trailerTop - 4} r={4} fill="#999" stroke="#555" strokeWidth={1} />
        </g>
      )}
      <rect x={trailerX} y={trailerTop} width={trailerW} height={trailerH} rx={5} fill="#d8d8d8" stroke="#999" strokeWidth={2} filter="url(#tshadow)" />
      <rect x={trailerX} y={trailerTop} width={trailerW} height={8} rx={3} fill="#bbb" />
      <rect x={trailerX} y={trailerBottom - 8} width={trailerW} height={8} rx={3} fill="#bbb" />
      <rect x={trailerX + 8} y={trailerTop + 8} width={6} height={trailerH - 16} fill="#c8c8c8" />
      <rect x={trailerX + trailerW - 14} y={trailerTop + 8} width={6} height={trailerH - 16} fill="#c8c8c8" />
      {front > 0 && (
        <line x1={trailerCX} y1={frontGroupTop + front * axleSpacing + 10} x2={trailerCX} y2={rearGroupTop - 10}
          stroke="#aaa" strokeWidth={1} strokeDasharray="4,4" />
      )}
      {axles.map(axle => renderAxle(axle))}
      <rect x={trailerX + 4} y={trailerBottom - 6} width={trailerW - 8} height={8} rx={2} fill="#888" />
      <rect x={trailerX + 2} y={trailerBottom - 18} width={10} height={8} rx={2} fill="#ff4444" opacity={0.85} />
      <rect x={trailerX + trailerW - 12} y={trailerBottom - 18} width={10} height={8} rx={2} fill="#ff4444" opacity={0.85} />
    </svg>
  );
}

function SelectWithOther({ label, options, value, otherValue, onChange, onOtherChange }) {
  const isOther = value === 'Other';
  const inp = { width: '100%', padding: '11px 14px', borderRadius: 10, border: '1px solid var(--border)', fontSize: 14, boxSizing: 'border-box', marginBottom: isOther ? 6 : 12, background: 'var(--surface-input)', color: 'var(--text)', transition: 'all 0.2s' };
  const lbl = { fontWeight: 600, fontSize: 11, marginBottom: 6, display: 'block', color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.7px' };
  return (
    <div>
      <label style={lbl}>{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)} style={inp}>
        <option value="">— Select —</option>
        {options.map(o => <option key={o}>{o}</option>)}
      </select>
      {isOther && <input value={otherValue} onChange={e => onOtherChange(e.target.value)} placeholder={`Enter ${label.toLowerCase()}...`} style={{ ...inp, marginBottom: 12 }} />}
    </div>
  );
}

function compressImage(file, maxPx = 1600, quality = 0.8) {
  return new Promise(resolve => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxPx || height > maxPx) {
        if (width > height) { height = Math.round(height * maxPx / width); width = maxPx; }
        else { width = Math.round(width * maxPx / height); height = maxPx; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob(blob => resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' })), 'image/jpeg', quality);
    };
    img.src = url;
  });
}

function PhotoUpload({ photos, onChange }) {
  const fileRef = useRef();
  const handleFile = async (file) => {
    const compressed = await compressImage(file);
    onChange([...photos, compressed]);
  };
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ fontWeight: 600, fontSize: 11, marginBottom: 8, display: 'block', color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.7px' }}>Photos (up to 3)</label>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
        {photos.map((f, i) => (
          <div key={i} style={{ position: 'relative' }}>
            <img src={URL.createObjectURL(f)} alt="" style={{ width: 76, height: 76, objectFit: 'cover', borderRadius: 10, border: '1px solid rgba(255,255,255,0.12)' }} />
            <button onClick={() => onChange(photos.filter((_, j) => j !== i))}
              style={{ position: 'absolute', top: -6, right: -6, background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', fontSize: 13, padding: 0, lineHeight: 1, boxShadow: '0 2px 6px rgba(239,68,68,0.4)' }}>×</button>
          </div>
        ))}
        {photos.length < 3 && (
          <button onClick={() => fileRef.current.click()}
            style={{ width: 76, height: 76, borderRadius: 10, border: '1.5px dashed var(--border2)', background: 'var(--surface)', cursor: 'pointer', fontSize: 22, color: 'var(--text3)', transition: 'all 0.2s' }}>+</button>
        )}
      </div>
      <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={e => { if (e.target.files[0] && photos.length < 3) { handleFile(e.target.files[0]); e.target.value = ''; } }} />
    </div>
  );
}

function DiagramLegend() {
  return (
    <div style={{ display: 'flex', gap: 16, marginTop: 12, justifyContent: 'center' }}>
      {[["#2a2a2a", "No record"], ["#4f8ef7", "Has record"], ["#fb923c", "Selected"]].map(([color, label]) => (
        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <svg width={14} height={10}><rect x={0} y={0} width={14} height={10} rx={2} fill={color} /></svg>
          <span style={{ fontSize: 11, color: 'var(--text2)', fontWeight: 500 }}>{label}</span>
        </div>
      ))}
    </div>
  );
}

function ChangePasswordForm() {
  const [current, setCurrent] = useState('');
  const [newPass, setNewPass] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const submit = async () => {
    setError(''); setSuccess('');
    if (newPass !== confirm) return setError('New passwords do not match');
    if (newPass.length < 6) return setError('Password must be at least 6 characters');
    try {
      // Passwords live in handy-auth (SSO), not this app — change it there so
      // the new password works across all handytl.com apps. Use a bare axios
      // instance: the global interceptor's Bearer token is signed for this app
      // and would fail handy-auth's verification; the SSO cookie is the auth.
      await axios.create().post('https://auth.handytl.com/api/change-password',
        { currentPassword: current, newPassword: newPass },
        { withCredentials: true }
      );
      setSuccess('Password changed successfully! This is your sign-in password for all Handy Truckline apps.');
      setCurrent(''); setNewPass(''); setConfirm('');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to change password');
    }
  };

  const inp = { width: '100%', padding: '11px 14px', borderRadius: 10, border: '1px solid var(--border)', fontSize: 14, boxSizing: 'border-box', marginBottom: 12, background: 'var(--surface-input)', color: 'var(--text)', transition: 'all 0.2s' };
  const lbl = { fontWeight: 600, fontSize: 11, marginBottom: 6, display: 'block', color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.7px' };

  return (
    <div>
      <h3 style={{ marginTop: 0, marginBottom: 20, fontSize: 16, color: 'var(--text)' }}>Change Password</h3>
      {error && <div style={{ background: 'rgba(248,113,113,0.12)', border: '1px solid rgba(248,113,113,0.3)', color: '#ef4444', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{error}</div>}
      {success && <div style={{ background: 'rgba(22,163,74,0.1)', border: '1px solid rgba(22,163,74,0.3)', color: '#16a34a', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{success}</div>}
      <label style={lbl}>Current Password</label>
      <input type="password" value={current} onChange={e => setCurrent(e.target.value)} style={inp} placeholder="••••••••" />
      <label style={lbl}>New Password</label>
      <input type="password" value={newPass} onChange={e => setNewPass(e.target.value)} style={inp} placeholder="••••••••" />
      <label style={lbl}>Confirm New Password</label>
      <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} style={inp} placeholder="••••••••" />
      <button onClick={submit}
        style={{ background: 'linear-gradient(135deg, #4f8ef7, #2563eb)', color: '#fff', border: 'none', padding: '11px 22px', borderRadius: 10, fontSize: 14, cursor: 'pointer', fontWeight: 600, boxShadow: '0 4px 14px rgba(79,142,247,0.3)', transition: 'all 0.2s' }}>
        Update Password
      </button>
    </div>
  );
}

function RotateModal({ isOpen, onClose, installA, installB, rotateForm, setRotateForm, submitRotation, saved }) {
  if (!isOpen) return null;
  const inp = { width: '100%', padding: '11px 14px', borderRadius: 10, border: '1px solid var(--border)', fontSize: 15, boxSizing: 'border-box', marginBottom: 10, background: 'var(--surface-input)', color: 'var(--text)', transition: 'all 0.2s' };
  const lbl = { fontWeight: 600, fontSize: 11, marginBottom: 6, display: 'block', color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.7px' };
  const noTire = !installA || !installB;
  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'var(--modal-overlay)', zIndex: 100, backdropFilter: 'blur(4px)' }} />
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 101, background: 'var(--modal-bg)', borderRadius: '22px 22px 0 0', padding: '20px 22px 40px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 -12px 60px rgba(0,0,0,0.6)', border: '1px solid var(--border)', borderBottom: 'none' }}>
        <div style={{ width: 36, height: 4, background: 'var(--modal-handle)', borderRadius: 99, margin: '0 auto 18px' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <span style={{ fontWeight: 700, color: '#4f8ef7', fontSize: 15 }}>Tire Rotation</span>
          <button onClick={onClose} style={{ background: 'var(--surface2)', border: 'none', borderRadius: '50%', width: 32, height: 32, fontSize: 18, cursor: 'pointer', color: 'var(--text2)', lineHeight: 1 }}>×</button>
        </div>
        {noTire ? (
          <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#f87171', padding: 14, borderRadius: 10, textAlign: 'center', fontSize: 14 }}>
            Both positions must have an active tire to rotate.
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20, padding: 14, background: 'var(--surface2)', borderRadius: 12, border: '1px solid var(--border)' }}>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#4f8ef7' }}>Axle {installA.axle} — {installA.position}</div>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 3 }}>{installA.brand} {installA.size}</div>
              </div>
              <div style={{ fontSize: 22, color: 'var(--text3)', fontWeight: 300 }}>↔</div>
              <div style={{ flex: 1, textAlign: 'center' }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#4f8ef7' }}>Axle {installB.axle} — {installB.position}</div>
                <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 3 }}>{installB.brand} {installB.size}</div>
              </div>
            </div>
            <div style={{ fontWeight: 700, fontSize: 11, color: 'var(--section2-color)', borderBottom: '1px solid var(--section2-border)', paddingBottom: 6, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.7px' }}>Tread at Rotation</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 4 }}>
              <div>
                <label style={lbl}>Axle {installA.axle} {installA.position}</label>
                <select value={rotateForm.tread_a} onChange={e => setRotateForm({ ...rotateForm, tread_a: e.target.value })} style={inp}>
                  <option value="">-- Select --</option>
                  {[...TREADS].reverse().map(n => <option key={n} value={n}>{n}/32</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Axle {installB.axle} {installB.position}</label>
                <select value={rotateForm.tread_b} onChange={e => setRotateForm({ ...rotateForm, tread_b: e.target.value })} style={inp}>
                  <option value="">-- Select --</option>
                  {[...TREADS].reverse().map(n => <option key={n} value={n}>{n}/32</option>)}
                </select>
              </div>
            </div>
            <label style={lbl}>Technician</label>
            <input value={rotateForm.technician} onChange={e => setRotateForm({ ...rotateForm, technician: e.target.value })} style={inp} placeholder="Name" />
            <label style={lbl}>Notes <span style={{ fontWeight: 400, color: 'var(--text3)' }}>optional</span></label>
            <input value={rotateForm.notes} onChange={e => setRotateForm({ ...rotateForm, notes: e.target.value })} style={inp} placeholder="Optional" />
            <div style={{ margin: '4px 0 14px' }}>
              <button type="button" onClick={() => setRotateForm({ ...rotateForm, overrideMilesDate: !rotateForm.overrideMilesDate, overrideMiles: '', overrideDate: '' })} style={{ background: rotateForm.overrideMilesDate ? '#4f8ef7' : 'var(--surface2)', color: rotateForm.overrideMilesDate ? '#fff' : 'var(--text2)', border: `1px solid ${rotateForm.overrideMilesDate ? '#4f8ef7' : 'var(--border)'}`, borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {rotateForm.overrideMilesDate ? '✓ Using Past Miles / Date' : 'Use Past Miles / Date'}
              </button>
              {rotateForm.overrideMilesDate && (
                <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={lbl}>Miles at Rotation</label>
                    <input type="number" inputMode="numeric" value={rotateForm.overrideMiles} onChange={e => setRotateForm({ ...rotateForm, overrideMiles: e.target.value })} style={inp} placeholder="e.g. 142500" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={lbl}>Date of Rotation</label>
                    <input type="date" value={rotateForm.overrideDate} onChange={e => setRotateForm({ ...rotateForm, overrideDate: e.target.value })} style={inp} />
                  </div>
                </div>
              )}
            </div>
            {saved && <div style={{ background: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.3)', color: '#6ee7b7', padding: '10px 14px', borderRadius: 8, marginBottom: 14, textAlign: 'center', fontWeight: 700 }}>✓ Saved!</div>}
            <button onClick={submitRotation} style={{ background: 'linear-gradient(135deg,#4f8ef7,#2563eb)', color: '#fff', border: 'none', padding: '14px 20px', borderRadius: 10, fontSize: 15, cursor: 'pointer', fontWeight: 700, width: '100%', marginTop: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.35)' }}>Confirm Rotation</button>
          </>
        )}
      </div>
    </>
  );
}

function TireModal({ selectedTires, isOpen, onClose, formType, setFormType, saved, repForm, setRepForm, insForm, setInsForm, submitReplacement, submitInspection, scanning, scanInputRef, handleScan, analyzingTread, treadScanInputRef, handleTreadScan, treadAnalysis, setTreadAnalysis, voiceRecording, voiceParsing, voiceTranscript, startVoice, stopVoice, perTireData, setPerTireData }) {
  if (!isOpen || !selectedTires || !selectedTires.length) return null;

  const inp = { width: '100%', padding: '11px 14px', borderRadius: 10, border: '1px solid var(--border)', fontSize: 15, boxSizing: 'border-box', marginBottom: 10, background: 'var(--surface-input)', color: 'var(--text)', transition: 'all 0.2s' };
  const lbl = { fontWeight: 600, fontSize: 11, marginBottom: 6, display: 'block', color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.7px' };
  const btn = (color) => {
    const g = { '#e05c2a': 'linear-gradient(135deg,#fb923c,#c2410c)', '#1a73e8': 'linear-gradient(135deg,#4f8ef7,#2563eb)', '#6a1b9a': 'linear-gradient(135deg,#a78bfa,#5b21b6)', '#2e7d32': 'linear-gradient(135deg,#34d399,#065f46)', '#555': 'linear-gradient(135deg,#6b7280,#374151)', '#e53935': 'linear-gradient(135deg,#f87171,#dc2626)' };
    return { background: g[color] || color, color: '#fff', border: 'none', padding: '14px 20px', borderRadius: 10, fontSize: 15, cursor: 'pointer', fontWeight: 700, width: '100%', marginTop: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.35)', transition: 'all 0.2s' };
  };

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'var(--modal-overlay)', zIndex: 100, backdropFilter: 'blur(4px)' }} />
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 101,
        background: 'var(--modal-bg)', borderRadius: '22px 22px 0 0',
        padding: '20px 22px 40px', maxHeight: '90vh', overflowY: 'auto',
        boxShadow: '0 -12px 60px rgba(0,0,0,0.6)', border: '1px solid var(--border)', borderBottom: 'none'
      }}>
        <div style={{ width: 36, height: 4, background: 'var(--modal-handle)', borderRadius: 99, margin: '0 auto 18px' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
          <span style={{ fontWeight: 700, color: 'var(--section-color)', fontSize: 15 }}>
            {selectedTires.length === 1
              ? `Axle ${selectedTires[0].axle} — ${selectedTires[0].position}`
              : `${selectedTires.length} tires selected`}
          </span>
          <button onClick={onClose} style={{ background: 'var(--surface2)', border: 'none', borderRadius: '50%', width: 32, height: 32, fontSize: 18, cursor: 'pointer', color: 'var(--text2)', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ display: 'flex', gap: 6, marginBottom: 18, background: 'var(--surface2)', borderRadius: 12, padding: 4 }}>
          <button onClick={() => setFormType('replacement')}
            style={{ flex: 1, padding: '10px', borderRadius: 9, border: 'none', background: formType === 'replacement' ? 'linear-gradient(135deg,#fb923c,#c2410c)' : 'transparent', color: formType === 'replacement' ? '#fff' : 'var(--text2)', fontWeight: 700, fontSize: 14, cursor: 'pointer', boxShadow: formType === 'replacement' ? '0 4px 12px rgba(251,146,60,0.3)' : 'none', transition: 'all 0.2s' }}>
            Replacement
          </button>
          <button onClick={() => setFormType('inspection')}
            style={{ flex: 1, padding: '10px', borderRadius: 9, border: 'none', background: formType === 'inspection' ? 'linear-gradient(135deg,#4f8ef7,#2563eb)' : 'transparent', color: formType === 'inspection' ? '#fff' : 'var(--text2)', fontWeight: 700, fontSize: 14, cursor: 'pointer', boxShadow: formType === 'inspection' ? '0 4px 12px rgba(79,142,247,0.3)' : 'none', transition: 'all 0.2s' }}>
            Inspection
          </button>
        </div>

        {saved && <div style={{ background: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.3)', color: '#6ee7b7', padding: '10px 14px', borderRadius: 8, marginBottom: 14, textAlign: 'center', fontWeight: 700 }}>✓ Saved!</div>}

        {formType === 'replacement' && (
          <div>
            <div style={{ fontWeight: 700, fontSize: 11, color: 'var(--section-color)', borderBottom: '1px solid var(--section-border)', paddingBottom: 6, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.7px' }}>Tire Coming Off</div>
            {selectedTires.length <= 1 ? (
              <>
                <SelectWithOther label="Reason for Replacement" options={REPLACEMENT_REASONS} value={repForm.reason} otherValue={repForm.reasonOther} onChange={v => setRepForm({ ...repForm, reason: v })} onOtherChange={v => setRepForm({ ...repForm, reasonOther: v })} />
                <label style={lbl}>Tread Depth at Removal (32nds)</label>
                <select value={repForm.removal_tread_depth} onChange={e => setRepForm({ ...repForm, removal_tread_depth: e.target.value })} style={inp}>
                  <option value="">-- Select --</option>
                  {[...TREADS].reverse().map(n => <option key={n} value={n}>{n}/32</option>)}
                </select>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 14px', padding: '10px 14px', background: 'var(--surface2)', borderRadius: 10, border: '1px solid var(--border)' }}>
                  <input type="checkbox" id="is_roadside" checked={repForm.is_roadside} onChange={e => setRepForm({ ...repForm, is_roadside: e.target.checked })} style={{ width: 17, height: 17, cursor: 'pointer', accentColor: '#fb923c' }} />
                  <label htmlFor="is_roadside" style={{ fontSize: 14, color: 'var(--text)', cursor: 'pointer', fontWeight: 500 }}>Roadside</label>
                </div>
              </>
            ) : (
              perTireData.map((td, i) => (
                <div key={`${td.axle}-${td.position}`} style={{ marginBottom: 12, padding: '10px 12px', background: 'var(--surface2)', borderRadius: 10, border: '1px solid var(--border)' }}>
                  <div style={{ fontWeight: 600, fontSize: 12, color: 'var(--section-color)', marginBottom: 8 }}>Axle {td.axle} — {td.position}</div>
                  <SelectWithOther label="Reason" options={REPLACEMENT_REASONS} value={td.reason} otherValue={td.reasonOther} onChange={v => setPerTireData(prev => prev.map((p, j) => j === i ? { ...p, reason: v } : p))} onOtherChange={v => setPerTireData(prev => prev.map((p, j) => j === i ? { ...p, reasonOther: v } : p))} />
                  <label style={lbl}>Tread at Removal (32nds)</label>
                  <select value={td.removal_tread_depth} onChange={e => setPerTireData(prev => prev.map((p, j) => j === i ? { ...p, removal_tread_depth: e.target.value } : p))} style={inp}>
                    <option value="">-- Select --</option>
                    {[...TREADS].reverse().map(n => <option key={n} value={n}>{n}/32</option>)}
                  </select>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 0' }}>
                    <input type="checkbox" id={`roadside_${i}`} checked={td.is_roadside} onChange={e => setPerTireData(prev => prev.map((p, j) => j === i ? { ...p, is_roadside: e.target.checked } : p))} style={{ width: 17, height: 17, cursor: 'pointer', accentColor: '#fb923c' }} />
                    <label htmlFor={`roadside_${i}`} style={{ fontSize: 14, color: 'var(--text)', cursor: 'pointer', fontWeight: 500 }}>Roadside</label>
                  </div>
                </div>
              ))
            )}
            <PhotoUpload photos={repForm.photos} onChange={photos => setRepForm({ ...repForm, photos })} />
            <div style={{ fontWeight: 700, fontSize: 11, color: 'var(--section2-color)', borderBottom: '1px solid var(--section2-border)', paddingBottom: 6, marginTop: 18, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.7px' }}>Tire Going On</div>
            <input ref={scanInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleScan} />
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button type="button" onClick={() => scanInputRef.current.click()} disabled={scanning} style={{ ...btn('#6a1b9a'), flex: 1, fontSize: 13, marginTop: 0 }}>
                {scanning ? '⏳ Scanning...' : '📷 Scan Tire Label (AI)'}
              </button>
              <button
                type="button"
                onPointerDown={() => startVoice('replacement')}
                onPointerUp={stopVoice}
                onPointerLeave={stopVoice}
                disabled={voiceParsing}
                style={{ ...btn(voiceRecording ? '#e53935' : '#555'), flex: 0, padding: '10px 14px', fontSize: 18, marginTop: 0, whiteSpace: 'nowrap', transition: 'all 0.15s', userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}
                title="Hold to dictate tire info"
              >
                {voiceRecording ? '🔴' : voiceParsing ? '⏳' : '🎙️'}
              </button>
            </div>
            {voiceTranscript && !voiceRecording && !voiceParsing && (
              <div style={{ background: 'var(--voice-bg)', border: '1px solid var(--voice-border)', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 12, color: 'var(--text2)', fontStyle: 'italic' }}>
                "{voiceTranscript}"
              </div>
            )}
            <SelectWithOther label="Tire Brand" options={BRANDS} value={repForm.brand} otherValue={repForm.brandOther} onChange={v => setRepForm({ ...repForm, brand: v })} onOtherChange={v => setRepForm({ ...repForm, brandOther: v })} />
            <SelectWithOther label="Tire Size" options={SIZES} value={repForm.size} otherValue={repForm.sizeOther} onChange={v => setRepForm({ ...repForm, size: v })} onOtherChange={v => setRepForm({ ...repForm, sizeOther: v })} />
            <SelectWithOther label="Tread Pattern" options={TREAD_PATTERNS} value={repForm.tread_pattern} otherValue={repForm.treadPatternOther} onChange={v => setRepForm({ ...repForm, tread_pattern: v })} onOtherChange={v => setRepForm({ ...repForm, treadPatternOther: v })} />
            <label style={lbl}>Tire Pressure (PSI)</label>
            <input type="number" inputMode="numeric" value={repForm.tire_pressure} onChange={e => setRepForm({ ...repForm, tire_pressure: e.target.value })} style={inp} placeholder="e.g. 105" required />
            <label style={lbl}>Tread Depth at Install (32nds)</label>
            <select value={repForm.tread_depth} onChange={e => setRepForm({ ...repForm, tread_depth: e.target.value })} style={inp}>
              <option value="">-- Select --</option>
              {TREADS.map(n => <option key={n} value={n}>{n}/32</option>)}
            </select>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 14px', padding: '10px 14px', background: 'var(--surface2)', borderRadius: 10, border: '1px solid var(--border)' }}>
              <input type="checkbox" id="is_retread" checked={repForm.is_retread} onChange={e => setRepForm({ ...repForm, is_retread: e.target.checked })} style={{ width: 17, height: 17, cursor: 'pointer', accentColor: '#4f8ef7' }} />
              <label htmlFor="is_retread" style={{ fontSize: 14, color: 'var(--text)', cursor: 'pointer', fontWeight: 500 }}>Retread</label>
            </div>
            <SelectWithOther label="Vendor (optional)" options={VENDORS} value={repForm.vendor} otherValue={repForm.vendorOther} onChange={v => setRepForm({ ...repForm, vendor: v })} onOtherChange={v => setRepForm({ ...repForm, vendorOther: v })} />
            <label style={lbl}>Notes <span style={{ fontWeight: 400, color: 'var(--text3)' }}>optional</span></label>
            <input value={repForm.notes || ''} onChange={e => setRepForm({ ...repForm, notes: e.target.value })} style={inp} placeholder="Optional" />
            <label style={lbl}>Technician</label>
            <input value={repForm.technician} onChange={e => setRepForm({ ...repForm, technician: e.target.value })} style={inp} placeholder="Name" />
            <div style={{ margin: '4px 0 14px' }}>
              <button type="button" onClick={() => setRepForm({ ...repForm, overrideMilesDate: !repForm.overrideMilesDate, overrideMiles: '', overrideDate: '' })} style={{ background: repForm.overrideMilesDate ? '#4f8ef7' : 'var(--surface2)', color: repForm.overrideMilesDate ? '#fff' : 'var(--text2)', border: `1px solid ${repForm.overrideMilesDate ? '#4f8ef7' : 'var(--border)'}`, borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}>
                {repForm.overrideMilesDate ? '✓ Using Past Miles / Date' : 'Use Past Miles / Date'}
              </button>
              {repForm.overrideMilesDate && (
                <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label style={lbl}>Miles at Replacement</label>
                    <input type="number" inputMode="numeric" value={repForm.overrideMiles} onChange={e => setRepForm({ ...repForm, overrideMiles: e.target.value })} style={inp} placeholder="e.g. 142500" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label style={lbl}>Date of Replacement</label>
                    <input type="date" value={repForm.overrideDate} onChange={e => setRepForm({ ...repForm, overrideDate: e.target.value })} style={inp} />
                  </div>
                </div>
              )}
            </div>
            <button onClick={submitReplacement} style={btn('#e05c2a')}>Save {selectedTires.length > 1 ? `${selectedTires.length} Replacements` : 'Replacement'}</button>
          </div>
        )}

        {formType === 'inspection' && (
          <div>
            <input ref={treadScanInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleTreadScan} />
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <button type="button" onClick={() => treadScanInputRef.current.click()} disabled={analyzingTread} style={{ ...btn('#2e7d32'), flex: 1, fontSize: 13, marginTop: 0 }}>
                {analyzingTread ? '⏳ Analyzing...' : '📷 Analyze Tread (AI)'}
              </button>
              <button
                type="button"
                onPointerDown={() => startVoice('inspection')}
                onPointerUp={stopVoice}
                onPointerLeave={stopVoice}
                disabled={voiceParsing}
                style={{ ...btn(voiceRecording ? '#e53935' : '#555'), flex: 0, padding: '10px 14px', fontSize: 18, marginTop: 0, whiteSpace: 'nowrap', transition: 'all 0.15s', userSelect: 'none', WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}
                title="Hold to dictate inspection info"
              >
                {voiceRecording ? '🔴' : voiceParsing ? '⏳' : '🎙️'}
              </button>
            </div>
            {voiceTranscript && !voiceRecording && !voiceParsing && (
              <div style={{ background: 'var(--voice-bg)', border: '1px solid var(--voice-border)', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 12, color: 'var(--text2)', fontStyle: 'italic' }}>
                "{voiceTranscript}"
              </div>
            )}
            {treadAnalysis && (
              <div style={{ background: 'var(--tread-bg)', border: '1px solid var(--tread-border)', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 12, color: '#34d399' }}>
                AI estimated: {treadAnalysis.tread_depth}/32 · {treadAnalysis.condition}{treadAnalysis.wear_notes ? ` · ${treadAnalysis.wear_notes}` : ''} — verify before saving
              </div>
            )}
            <label style={lbl}>Tread Depth (32nds)</label>
            <select value={insForm.tread_depth} onChange={e => setInsForm({ ...insForm, tread_depth: e.target.value })} style={inp}>
              <option value="">-- Select --</option>
              {TREADS.map(n => <option key={n} value={n}>{n}/32</option>)}
            </select>
            <label style={lbl}>Condition</label>
            <select value={insForm.condition} onChange={e => setInsForm({ ...insForm, condition: e.target.value })} style={inp}>
              {CONDITIONS.map(c => <option key={c}>{c}</option>)}
            </select>
            <label style={lbl}>Technician</label>
            <input value={insForm.technician} onChange={e => setInsForm({ ...insForm, technician: e.target.value })} style={inp} placeholder="Name" />
            <label style={lbl}>Notes</label>
            <input value={insForm.notes} onChange={e => setInsForm({ ...insForm, notes: e.target.value })} style={inp} placeholder="Optional" />
            <label style={lbl}>Tire Pressure (PSI) <span style={{ fontWeight: 400, color: 'var(--text3)' }}>optional</span></label>
            <input type="number" inputMode="numeric" value={insForm.tire_pressure} onChange={e => setInsForm({ ...insForm, tire_pressure: e.target.value })} style={inp} placeholder="e.g. 105" />
            <PhotoUpload photos={insForm.photos} onChange={photos => setInsForm({ ...insForm, photos })} />
            <button onClick={submitInspection} style={btn('#1a73e8')}>Save {selectedTires.length > 1 ? `${selectedTires.length} Inspections` : 'Inspection'}</button>
          </div>
        )}
      </div>
    </>
  );
}

function App() {
  const [user, setUser] = useState(getUser);
  const [view, setView] = useState('dashboard');
  const [vehicles, setVehicles] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [search, setSearch] = useState('');
  const [fleetTab, setFleetTab] = useState('truck');
  const [userLocation, setUserLocation] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState({ installs: [], inspections: [], rotations: [] });
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [formType, setFormType] = useState('replacement');
  const [saved, setSaved] = useState(false);
  const [selectedTires, setSelectedTires] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [driveAxleConfig, setDriveAxleConfig] = useState({});
  const [axleCount, setAxleCount] = useState(null);
  const [axleCountSet, setAxleCountSet] = useState(false);
  const [trailerConfig, setTrailerConfig] = useState(null);
  const [trailerConfigSet, setTrailerConfigSet] = useState(false);
  const [repForm, setRepForm] = useState({ brand: '', brandOther: '', size: '', sizeOther: '', vendor: '', vendorOther: '', tread_pattern: '', treadPatternOther: '', technician: '', tread_depth: '', reason: '', reasonOther: '', removal_tread_depth: '', is_retread: false, is_roadside: false, tire_pressure: '', notes: '', photos: [], overrideMilesDate: false, overrideMiles: '', overrideDate: '' });
  const [insForm, setInsForm] = useState({ tread_depth: '', condition: 'Good', notes: '', technician: '', tire_pressure: '', photos: [] });
  const [perTireData, setPerTireData] = useState([]);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [rotateMode, setRotateMode] = useState(false);
  const [rotateTireA, setRotateTireA] = useState(null);
  const [rotateInstallA, setRotateInstallA] = useState(null);
  const [rotateInstallB, setRotateInstallB] = useState(null);
  const [rotateModalOpen, setRotateModalOpen] = useState(false);
  const [rotateForm, setRotateForm] = useState({ tread_a: '', tread_b: '', technician: '', notes: '', overrideMilesDate: false, overrideMiles: '', overrideDate: '' });
  const [rotateSaved, setRotateSaved] = useState(false);
  const [scanning, setScanning] = useState(false);
  const scanInputRef = useRef(null);
  const [analyzingTread, setAnalyzingTread] = useState(false);
  const treadScanInputRef = useRef(null);
  const [treadAnalysis, setTreadAnalysis] = useState(null);
  const [voiceRecording, setVoiceRecording] = useState(false);
  const [voiceParsing, setVoiceParsing] = useState(false);
  const [voiceTranscript, setVoiceTranscript] = useState('');
  const voiceMediaRecorder = useRef(null);
  const voiceChunks = useRef([]);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('ft_darkMode');
    if (saved !== null) return saved === 'true';
    return window.innerWidth < 768;
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [apps, setApps] = useState(() => { try { return JSON.parse(localStorage.getItem('ft_apps')); } catch { return null; } });
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 768);

  useEffect(() => {
    document.body.classList.toggle('dark', darkMode);
    localStorage.setItem('ft_darkMode', darkMode);
  }, [darkMode]);

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Always start loading — verify session via cookie (no localStorage needed)
  const [ssoLoading, setSsoLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get('sso_token');

    const finishWithUser = (u) => {
      localStorage.setItem('ft_user', JSON.stringify(u));
      setUser(u);
      setSsoLoading(false);
    };

    const redirectToAuth = () => {
      window.location.href = 'https://auth.handytl.com/login?redirect=' + encodeURIComponent(window.location.origin);
    };

    if (ssoToken) {
      // Exchange SSO token from auth redirect
      window.history.replaceState({}, '', window.location.pathname);
      axios.post(API + '/api/auth/sso-exchange', { sso_token: ssoToken })
        .then(res => {
          localStorage.setItem('ft_token', res.data.token);
          if (res.data.apps) { localStorage.setItem('ft_apps', JSON.stringify(res.data.apps)); setApps(res.data.apps); }
          finishWithUser(res.data.user);
        })
        .catch(redirectToAuth);
    } else {
      // No SSO token — try Bearer token from localStorage first, then cookie
      const savedToken = localStorage.getItem('ft_token');
      const headers = savedToken ? { Authorization: 'Bearer ' + savedToken } : {};
      axios.get(API + '/api/auth/me', { headers })
        .then(res => finishWithUser(res.data.user))
        .catch(redirectToAuth);
    }
  }, []);
  useEffect(() => {
    if (user) {
      setRepForm(f => ({ ...f, technician: user.name }));
      setInsForm(f => ({ ...f, technician: user.name }));
      setRotateForm(f => ({ ...f, technician: user.name }));
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/api/vehicles`, {}).then(res => setVehicles(res.data.data));
    axios.get(`${API}/api/dashboard`, {}).then(res => setDashboard(res.data));
  }, [user]);

  useEffect(() => {
    if (view === 'vehicles' && !userLocation && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => {}
      );
    }
  }, [view, userLocation]);

  useEffect(() => {
    setPerTireData(prev =>
      selectedTires.map(t =>
        prev.find(p => p.axle === t.axle && p.position === t.position) ||
        { axle: t.axle, position: t.position, reason: '', reasonOther: '', removal_tread_depth: '', is_roadside: false }
      )
    );
  }, [selectedTires]);

  const logout = async () => {
    localStorage.removeItem('ft_token');
    localStorage.removeItem('ft_user');
    localStorage.removeItem('ft_apps');
    // Clear the auth cookie on auth.handytl.com
    try { await axios.post('https://auth.handytl.com/api/logout', {}); } catch {}
    setUser(null);
    window.location.href = 'https://auth.handytl.com/login';
  };

  if (!user) {
    if (ssoLoading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#666' }}>Signing in…</div>;
    // Redirect to auth.handytl.com SSO
    window.location.href = 'https://auth.handytl.com/login?redirect=' + encodeURIComponent(window.location.origin);
    return null;
  }

  const handleScan = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await axios.post(`${API}/api/scan-tire-label`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { brand, size, tread_pattern } = res.data;
      const updates = {};
      const matchOption = (list, val) => list.find(o => o.toLowerCase() === val.toLowerCase()) || null;
      if (brand) { const m = matchOption(BRANDS, brand); if (m) updates.brand = m; else { updates.brand = 'Other'; updates.brandOther = brand; } }
      if (size) { const m = matchOption(SIZES, size); if (m) updates.size = m; else { updates.size = 'Other'; updates.sizeOther = size; } }
      if (tread_pattern) { const m = matchOption(TREAD_PATTERNS, tread_pattern); if (m) updates.tread_pattern = m; else { updates.tread_pattern = 'Other'; updates.treadPatternOther = tread_pattern; } }
      setRepForm(f => ({ ...f, ...updates }));
    } catch (e) {
      alert('Scan failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setScanning(false);
      e.target.value = '';
    }
  };

  const handleTreadScan = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAnalyzingTread(true);
    setTreadAnalysis(null);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await axios.post(`${API}/api/analyze-tread`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { tread_depth, condition, wear_notes } = res.data;
      const updates = {};
      if (tread_depth) updates.tread_depth = String(tread_depth);
      if (condition && ['Good', 'Fair', 'Poor', 'Replace Immediately'].includes(condition)) updates.condition = condition;
      if (wear_notes) updates.notes = wear_notes;
      setInsForm(f => ({ ...f, ...updates }));
      setTreadAnalysis(res.data);
    } catch (e) {
      alert('Analysis failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setAnalyzingTread(false);
      e.target.value = '';
    }
  };

  const startVoice = async (currentFormType) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      voiceChunks.current = [];
      const mr = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4' });
      voiceMediaRecorder.current = mr;
      mr.ondataavailable = e => { if (e.data.size > 0) voiceChunks.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        setVoiceRecording(false);
        setVoiceParsing(true);
        setVoiceTranscript('');
        try {
          const mimeType = voiceChunks.current[0]?.type || 'audio/webm';
          const blob = new Blob(voiceChunks.current, { type: mimeType });
          const ext = mimeType.includes('mp4') ? 'mp4' : 'webm';
          const fd = new FormData();
          fd.append('audio', blob, `voice.${ext}`);
          fd.append('formType', currentFormType);
          const res = await axios.post(`${API}/api/voice-parse`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          const { transcript, fields } = res.data;
          setVoiceTranscript(transcript);
          const matchOption = (list, val) => val ? (list.find(o => o.toLowerCase() === val.toLowerCase()) || null) : null;
          if (currentFormType === 'replacement') {
            const updates = {};
            if (fields.brand) { const m = matchOption(['Michelin', 'Other'], fields.brand); if (m) updates.brand = m; else { updates.brand = 'Other'; updates.brandOther = fields.brand; } }
            if (fields.size) { const m = matchOption(['11R22.5','11R24.5','425','385','295','285','275','Other'], fields.size); if (m) updates.size = m; else { updates.size = 'Other'; updates.sizeOther = fields.size; } }
            if (fields.tread_pattern) { const m = matchOption(['XLEZ+','XLEZ','XL Grip D','XDN2','XLED+','XLED','X Multi','Other'], fields.tread_pattern); if (m) updates.tread_pattern = m; else { updates.tread_pattern = 'Other'; updates.treadPatternOther = fields.tread_pattern; } }
            if (fields.vendor) { const m = matchOption(['Wickel','Purcell','Les Schwab','Other'], fields.vendor); if (m) updates.vendor = m; else { updates.vendor = 'Other'; updates.vendorOther = fields.vendor; } }
            if (fields.tread_at_install) updates.tread_depth = String(fields.tread_at_install);
            if (fields.removal_reason) { const m = matchOption(['Tread Depth','Irregular Wear','Blown','Rock Drill','Casing','Mismatch','Other'], fields.removal_reason); if (m) updates.reason = m; else { updates.reason = 'Other'; updates.reasonOther = fields.removal_reason; } }
            if (fields.tread_at_removal) updates.removal_tread_depth = String(fields.tread_at_removal);
            if (fields.is_retread !== null && fields.is_retread !== undefined) updates.is_retread = !!fields.is_retread;
            if (fields.is_roadside !== null && fields.is_roadside !== undefined) updates.is_roadside = !!fields.is_roadside;
            if (fields.technician) updates.technician = fields.technician;
            setRepForm(f => ({ ...f, ...updates }));
          } else {
            const updates = {};
            if (fields.tread_depth) updates.tread_depth = String(fields.tread_depth);
            if (fields.condition && ['Good','Fair','Poor','Replace Immediately'].includes(fields.condition)) updates.condition = fields.condition;
            if (fields.notes) updates.notes = fields.notes;
            if (fields.technician) updates.technician = fields.technician;
            setInsForm(f => ({ ...f, ...updates }));
          }
        } catch (e) {
          alert('Voice parse failed: ' + (e.response?.data?.error || e.message));
        } finally {
          setVoiceParsing(false);
        }
      };
      mr.start();
      setVoiceRecording(true);
    } catch (e) {
      alert('Microphone access denied: ' + e.message);
    }
  };

  const stopVoice = () => {
    if (voiceMediaRecorder.current && voiceMediaRecorder.current.state !== 'inactive') {
      voiceMediaRecorder.current.stop();
    }
  };

  const runSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/api/sync`, {}, {});
      const res = await axios.get(`${API}/api/vehicles`, {});
      setVehicles(res.data.data);
    } finally {
      setSyncing(false);
    }
  };

  const exportCSV = async (path, filename) => {
    const res = await axios.get(`${API}${path}`, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    window.URL.revokeObjectURL(url);
  };

  const selectVehicle = (v) => {
    setSelected(v);
    setView('detail');
    setSelectedTires([]);
    setModalOpen(false);
    // Load saved axle tire config
    try {
      const savedConfig = v.axle_tire_config ? JSON.parse(v.axle_tire_config) : {};
      setDriveAxleConfig(savedConfig);
    } catch {
      setDriveAxleConfig({});
    }
    if (v.type === 'truck') {
      setAxleCountSet(!!v.axle_count);
      setAxleCount(v.axle_count || null);
    } else {
      setTrailerConfigSet(!!v.trailer_config);
      setTrailerConfig(v.trailer_config || null);
    }
    axios.get(`${API}/api/vehicles/${v.id}/history`, {}).then(res => setHistory(res.data));
  };

  const saveAxleCount = (count) => {
    setAxleCount(count); setAxleCountSet(true);
    axios.patch(`${API}/api/vehicles/${selected.id}`, { axle_count: count }, {});
    setSelected({ ...selected, axle_count: count });
  };

  const saveTrailerConfig = (config) => {
    setTrailerConfig(config); setTrailerConfigSet(true);
    const { front, rear } = TRAILER_CONFIGS[config];
    axios.patch(`${API}/api/vehicles/${selected.id}`, { trailer_config: config, axle_count: front + rear }, {});
    setSelected({ ...selected, trailer_config: config });
  };

  const handleTireClick = (axle, position) => {
    if (rotateMode) {
      if (!rotateTireA) {
        setRotateTireA({ axle, position });
        setSelectedTires([{ axle, position }]);
      } else {
        if (axle === rotateTireA.axle && position === rotateTireA.position) return;
        const instA = history.installs.find(r => r.axle === rotateTireA.axle && r.position === rotateTireA.position && !r.removed_at);
        const instB = history.installs.find(r => r.axle === axle && r.position === position && !r.removed_at);
        setRotateInstallA(instA || null);
        setRotateInstallB(instB || null);
        setRotateModalOpen(true);
      }
    } else if (multiSelectMode) {
      setSelectedTires(prev => {
        const exists = prev.some(t => t.axle === axle && t.position === position);
        return exists ? prev.filter(t => !(t.axle === axle && t.position === position)) : [...prev, { axle, position }];
      });
    } else {
      setSelectedTires([{ axle, position }]);
      setModalOpen(true);
    }
  };

  const submitRotation = async () => {
    if (!rotateInstallA || !rotateInstallB) { alert('Both positions must have an active tire.'); return; }
    try {
      await axios.post(`${API}/api/tire-rotations`, {
        vehicle_id: selected.id,
        install_id_a: rotateInstallA.id,
        install_id_b: rotateInstallB.id,
        from_axle_a: rotateInstallA.axle, from_position_a: rotateInstallA.position,
        from_axle_b: rotateInstallB.axle, from_position_b: rotateInstallB.position,
        tread_a: rotateForm.tread_a || null,
        tread_b: rotateForm.tread_b || null,
        technician: rotateForm.technician,
        notes: rotateForm.notes,
        override_odometer: rotateForm.overrideMilesDate ? rotateForm.overrideMiles : null,
        override_date: rotateForm.overrideMilesDate ? rotateForm.overrideDate : null,
      });
      setRotateSaved(true); setTimeout(() => setRotateSaved(false), 3000);
      axios.get(`${API}/api/vehicles/${selected.id}/history`, {}).then(res => setHistory(res.data));
      setRotateModalOpen(false);
      setRotateTireA(null); setRotateInstallA(null); setRotateInstallB(null);
      setSelectedTires([]); setRotateMode(false);
      setRotateForm(f => ({ ...f, tread_a: '', tread_b: '', notes: '', overrideMilesDate: false, overrideMiles: '', overrideDate: '' }));
    } catch (e) {
      alert('Error saving rotation: ' + (e.response?.data?.error || e.message));
    }
  };

  const handleChangeAxleConfig = (axleNum) => {
    const current = driveAxleConfig[axleNum];
    const newConfig = current === 'dual' ? 'single' : 'dual';
    if (!window.confirm(`Switch Axle ${axleNum} from ${current || 'unset'} to ${newConfig}? This will change the tire positions shown.`)) return;
    const updated = { ...driveAxleConfig, [axleNum]: newConfig };
    setDriveAxleConfig(updated);
    axios.patch(`${API}/api/vehicles/${selected.id}`, { axle_tire_config: JSON.stringify(updated) }, {});
  };

  // eslint-disable-next-line
  const setDriveConfig = (axle, config) => {
    const updated = { ...driveAxleConfig, [axle]: config };
    setDriveAxleConfig(updated);
    // Save to database
    axios.patch(`${API}/api/vehicles/${selected.id}`, { axle_tire_config: JSON.stringify(updated) }, {});
  };

  const submitReplacement = async () => {
    if (!selectedTires || !selectedTires.length) return;
    if (!repForm.tire_pressure) { alert('Tire pressure is required.'); return; }
    try {
      for (let i = 0; i < selectedTires.length; i++) {
        const tire = selectedTires[i];
        const td = selectedTires.length > 1 ? (perTireData[i] || {}) : null;
        const fd = new FormData();
        fd.append('vehicle_id', selected.id);
        fd.append('axle', tire.axle);
        fd.append('position', tire.position);
        fd.append('brand', repForm.brand === 'Other' ? repForm.brandOther : repForm.brand);
        fd.append('size', repForm.size === 'Other' ? repForm.sizeOther : repForm.size);
        fd.append('vendor', repForm.vendor === 'Other' ? repForm.vendorOther : repForm.vendor);
        fd.append('technician', repForm.technician);
        fd.append('tread_pattern', repForm.tread_pattern === 'Other' ? repForm.treadPatternOther : repForm.tread_pattern);
        fd.append('tread_at_install', repForm.tread_depth);
        fd.append('removal_reason', td ? (td.reason === 'Other' ? td.reasonOther : td.reason) : (repForm.reason === 'Other' ? repForm.reasonOther : repForm.reason));
        fd.append('tread_at_removal', td ? td.removal_tread_depth : repForm.removal_tread_depth);
        fd.append('is_retread', repForm.is_retread);
        fd.append('is_roadside', td ? td.is_roadside : repForm.is_roadside);
        if (repForm.tire_pressure) fd.append('tire_pressure', repForm.tire_pressure);
        if (repForm.notes) fd.append('notes', repForm.notes);
        if (repForm.overrideMilesDate) {
          if (repForm.overrideMiles) fd.append('override_odometer', repForm.overrideMiles);
          if (repForm.overrideDate) fd.append('override_date', repForm.overrideDate);
        }
        repForm.photos.forEach(f => fd.append('photos', f));
        await axios.post(`${API}/api/tire-installs`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setSaved(true); setTimeout(() => setSaved(false), 3000);
      axios.get(`${API}/api/vehicles/${selected.id}/history`, {}).then(res => setHistory(res.data));
      axios.get(`${API}/api/dashboard`, {}).then(res => setDashboard(res.data));
      setRepForm({ brand: '', brandOther: '', size: '', sizeOther: '', vendor: '', vendorOther: '', tread_pattern: '', treadPatternOther: '', technician: user.name, tread_depth: '', reason: '', reasonOther: '', removal_tread_depth: '', is_retread: false, is_roadside: false, tire_pressure: '', notes: '', photos: [], overrideMilesDate: false, overrideMiles: '', overrideDate: '' });
      setSelectedTires([]);
    } catch (e) {
      alert('Error saving replacement: ' + (e.response?.data?.error || e.message));
    }
  };

  const submitInspection = async () => {
    if (!selectedTires || !selectedTires.length) return;
    try {
      for (const tire of selectedTires) {
        const fd = new FormData();
        fd.append('vehicle_id', selected.id);
        fd.append('axle', tire.axle);
        fd.append('position', tire.position);
        fd.append('tread_depth', insForm.tread_depth);
        fd.append('condition', insForm.condition);
        fd.append('notes', insForm.notes);
        fd.append('technician', insForm.technician);
        if (insForm.tire_pressure) fd.append('tire_pressure', insForm.tire_pressure);
        insForm.photos.forEach(f => fd.append('photos', f));
        await axios.post(`${API}/api/inspections`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      setSaved(true); setTimeout(() => setSaved(false), 3000);
      axios.get(`${API}/api/vehicles/${selected.id}/history`, {}).then(res => setHistory(res.data));
      axios.get(`${API}/api/dashboard`, {}).then(res => setDashboard(res.data));
      setInsForm({ tread_depth: '', condition: 'Good', notes: '', technician: user.name, tire_pressure: '', photos: [] });
      setSelectedTires([]);
    } catch (e) {
      alert('Error saving inspection: ' + (e.response?.data?.error || e.message));
    }
  };

  const filtered = vehicles.filter(v => v.type === fleetTab).filter(v =>
    v.name.toLowerCase().includes(search.toLowerCase()) ||
    (v.license_plate || '').toLowerCase().includes(search.toLowerCase())
  );
  const trucks = vehicles.filter(v => v.type === 'truck');
  const trailers = vehicles.filter(v => v.type === 'trailer');

  const haversine = (lat1, lon1, lat2, lon2) => {
    const R = 3958.8, toRad = d => d * Math.PI / 180;
    const dLat = toRad(lat2 - lat1), dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat/2)**2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon/2)**2;
    return R * 2 * Math.asin(Math.sqrt(a));
  };
  const nearestVehicles = userLocation
    ? vehicles
        .filter(v => v.lat && v.lng && !/^old\s/i.test(v.name))
        .map(v => ({ ...v, dist: haversine(userLocation.lat, userLocation.lng, v.lat, v.lng) }))
        .sort((a, b) => a.dist - b.dist)
        .slice(0, 5)
    : [];
  const allRecords = [...(history.installs || []), ...(history.inspections || [])];

  const inp = { width: '100%', padding: '11px 14px', borderRadius: 10, border: '1px solid var(--border)', fontSize: 14, marginBottom: 12, background: 'var(--surface-input)', color: 'var(--text)', transition: 'all 0.2s' };
  const lbl = { fontWeight: 600, fontSize: 11, marginBottom: 6, display: 'block', color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.7px' };
  const btn = (color) => {
    const g = { '#1a73e8': 'linear-gradient(135deg,#4f8ef7,#2563eb)', '#e05c2a': 'linear-gradient(135deg,#fb923c,#c2410c)', '#2e7d32': 'linear-gradient(135deg,#34d399,#065f46)', '#1565c0': 'linear-gradient(135deg,#60a5fa,#1d4ed8)', '#6a1b9a': 'linear-gradient(135deg,#a78bfa,#5b21b6)', '#dc2626': 'linear-gradient(135deg,#f87171,#991b1b)', '#eee': 'var(--surface2)', '#ccc': 'var(--surface2)' };
    return { background: g[color] || color, color: color === '#eee' || color === '#ccc' ? 'var(--text2)' : '#fff', border: 'none', padding: '10px 20px', borderRadius: 10, fontSize: 14, cursor: 'pointer', fontWeight: 600, boxShadow: (color === '#eee' || color === '#ccc') ? 'none' : '0 4px 14px rgba(0,0,0,0.3)', transition: 'all 0.2s' };
  };
  const tabBtn = (active) => ({ background: active ? 'var(--hist-ins-bg)' : 'var(--surface)', color: active ? 'var(--section2-color)' : 'var(--text2)', border: '1px solid', borderColor: active ? 'var(--hist-ins-bd)' : 'var(--border)', padding: '8px 18px', borderRadius: 10, fontSize: 13, cursor: 'pointer', fontWeight: 600, transition: 'all 0.2s' });

  // ── Sidebar ──────────────────────────────────────────────────────────────────
  const SB_BG     = 'hsl(222,47%,11%)';
  const SB_BORDER = 'hsl(222,35%,18%)';
  const SB_TEXT   = 'hsl(213,27%,84%)';
  const SB_ACTIVE = 'hsl(220,86%,44%)';

  const OTHER_APPS = [
    { id: 'vm',   label: 'Vehicle Maintenance', href: 'https://auth.handytl.com/login?redirect=https://vm.handytl.com/login',   icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg> },
    { id: 'fuel', label: 'Fuel',                href: 'https://auth.handytl.com/login?redirect=https://fuel.handytl.com',        icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="22" x2="15" y2="22"/><line x1="4" y1="9" x2="14" y2="9"/><path d="M14 22V4a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v18"/><path d="M14 13h2a2 2 0 0 1 2 2v2a2 2 0 0 0 2 2h0a2 2 0 0 0 2-2V9.83a2 2 0 0 0-.59-1.42L18 5"/></svg> },
  ];
  const switchableApps = apps === null ? OTHER_APPS : OTHER_APPS.filter(a => apps.includes(a.id));

  const initials = user.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : (user.email || '?')[0].toUpperCase();

  const sbItem = (label, icon, isActive, onClick) => (
    <button key={label} onClick={() => { onClick(); setSidebarOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 12px', marginBottom: 2, borderRadius: 10, border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 14, fontWeight: isActive ? 600 : 500, background: isActive ? SB_ACTIVE : 'transparent', color: isActive ? '#fff' : SB_TEXT, boxShadow: isActive ? '0 2px 8px rgba(21,86,220,0.35)' : 'none', transition: 'all 0.15s' }}
      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}>
      <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{icon}</span>{label}
    </button>
  );

  const Sidebar = () => (
    <aside style={{ position: isDesktop ? 'sticky' : 'fixed', top: 0, left: isDesktop ? 0 : (sidebarOpen ? 0 : -288), width: isDesktop ? 256 : 288, height: '100vh', background: SB_BG, borderRight: `1px solid ${SB_BORDER}`, display: 'flex', flexDirection: 'column', transition: isDesktop ? 'none' : 'left 0.3s ease-in-out', zIndex: isDesktop ? 1 : 30, flexShrink: 0 }}>
      {/* Brand */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: `1px solid ${SB_BORDER}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: SB_ACTIVE, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/></svg>
          </div>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#fff', letterSpacing: '-0.3px' }}>Fleet Tires</div>
            <div style={{ fontSize: 10, color: SB_TEXT, opacity: 0.7, marginTop: 1 }}>Tire Tracker</div>
          </div>
        </div>
        {!isDesktop && (
          <button onClick={() => setSidebarOpen(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: SB_TEXT, padding: '4px 6px', borderRadius: 6, fontSize: 16 }}>✕</button>
        )}
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '12px', overflowY: 'auto' }}>
        {sbItem('Dashboard',
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
          view === 'dashboard', () => setView('dashboard'))}
        {sbItem('Fleet',
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>,
          view === 'vehicles', () => { setView('vehicles'); setSearch(''); })}
        {selected && sbItem(`📋 ${selected.name}`, null, view === 'detail', () => setView('detail'))}

        {user.role === 'admin' && (
          <>
            <div style={{ paddingTop: 16, paddingBottom: 6, paddingLeft: 12 }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: SB_TEXT, opacity: 0.5, textTransform: 'uppercase', letterSpacing: '1.2px', margin: 0 }}>Admin</p>
            </div>
            {sbItem(syncing ? 'Syncing…' : 'Sync Samsara',
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>,
              false, runSync)}
          </>
        )}
      </nav>

      {/* App switcher */}
      {switchableApps.length > 0 && (
        <div style={{ padding: '12px', borderTop: `1px solid ${SB_BORDER}` }}>
          <div style={{ paddingLeft: 12, paddingBottom: 6 }}>
            <p style={{ fontSize: 10, fontWeight: 700, color: SB_TEXT, opacity: 0.5, textTransform: 'uppercase', letterSpacing: '1.2px', margin: 0 }}>Switch App</p>
          </div>
          {switchableApps.map(a => (
            <a key={a.id} href={a.href} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, color: SB_TEXT, textDecoration: 'none', fontSize: 14, fontWeight: 500, transition: 'all 0.15s', marginBottom: 2 }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{a.icon}</span>{a.label}
            </a>
          ))}
        </div>
      )}

      {/* User footer */}
      <div style={{ padding: '12px', borderTop: `1px solid ${SB_BORDER}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', marginBottom: 4 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: SB_ACTIVE, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, flexShrink: 0, color: '#fff' }}>{initials}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name}</div>
            <div style={{ fontSize: 11, color: SB_TEXT, opacity: 0.6, textTransform: 'capitalize' }}>{user.role}</div>
          </div>
        </div>
        <button onClick={() => setDarkMode(d => !d)} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 12px', marginBottom: 2, borderRadius: 10, border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 14, fontWeight: 500, background: 'transparent', color: SB_TEXT, transition: 'all 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <span>{darkMode ? '☀️' : '🌙'}</span>{darkMode ? 'Light Mode' : 'Dark Mode'}
        </button>
        <button onClick={() => { setView('profile'); setSidebarOpen(false); }} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 12px', marginBottom: 2, borderRadius: 10, border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 14, fontWeight: 500, background: view === 'profile' ? SB_ACTIVE : 'transparent', color: view === 'profile' ? '#fff' : SB_TEXT, transition: 'all 0.15s' }}
          onMouseEnter={e => { if (view !== 'profile') e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
          onMouseLeave={e => { if (view !== 'profile') e.currentTarget.style.background = 'transparent'; }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>Profile
        </button>
        <button onClick={logout} style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '10px 12px', borderRadius: 10, border: 'none', cursor: 'pointer', textAlign: 'left', fontSize: 14, fontWeight: 500, background: 'transparent', color: '#f87171', transition: 'all 0.15s' }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(248,113,113,0.12)'}
          onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>Sign Out
        </button>
      </div>
    </aside>
  );

  // ── Render helpers ───────────────────────────────────────────────────────────
  const card = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: 20, marginBottom: 20 };
  const th = { padding: '11px 14px', fontWeight: 600, fontSize: 11, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.6px', borderBottom: '1px solid var(--table-border)', textAlign: 'left' };
  const td = (extra = {}) => ({ padding: '11px 14px', fontSize: 14, borderBottom: '1px solid var(--table-border)', color: 'var(--text)', ...extra });

  const renderContent = () => {
    if (view === 'profile') return (
      <div style={{ maxWidth: 980, margin: '20px auto', padding: '0 16px' }}>
        <h2 style={{ marginBottom: 24, fontSize: 22, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.3px' }}>My Profile</h2>
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: 28, maxWidth: 440 }}>
          <div style={{ marginBottom: 20, paddingBottom: 20, borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 600, fontSize: 11, color: 'var(--text2)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Name</div>
            <div style={{ fontSize: 16, color: 'var(--text)', fontWeight: 600 }}>{user.name}</div>
          </div>
          <div style={{ marginBottom: 28, paddingBottom: 24, borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 600, fontSize: 11, color: 'var(--text2)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Email</div>
            <div style={{ fontSize: 16, color: 'var(--text2)' }}>{user.email}</div>
          </div>
          <ChangePasswordForm />
        </div>
      </div>
    );

    if (view === 'dashboard') return (
      <div style={{ maxWidth: 980, margin: '20px auto', padding: '0 16px' }}>
        {!dashboard ? <p>Loading…</p> : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 24 }}>
              {[
                { val: trucks.length, label: 'Trucks', bg: 'var(--stat-truck-bg)', bd: 'var(--stat-truck-bd)', color: 'var(--stat-truck-c)', icon: '🚛' },
                { val: trailers.length, label: 'Trailers', bg: 'var(--stat-trailer-bg)', bd: 'var(--stat-trailer-bd)', color: 'var(--stat-trailer-c)', icon: <BoxTrailerIcon size={28} /> },
                { val: dashboard.replacementsThisMonth.length, label: 'Replacements This Month', bg: 'var(--stat-rep-bg)', bd: 'var(--stat-rep-bd)', color: 'var(--stat-rep-c)', icon: '🔄' },
              ].map(({ val, label, bg, bd, color, icon }) => (
                <div key={label} style={{ background: bg, border: `1px solid ${bd}`, borderRadius: 16, padding: '20px', backdropFilter: 'blur(10px)' }}>
                  <div style={{ fontSize: 24, marginBottom: 10 }}>{icon}</div>
                  <div style={{ fontSize: 36, fontWeight: 900, color, lineHeight: 1, letterSpacing: '-1px' }}>{val}</div>
                  <div style={{ fontSize: 12, color, marginTop: 8, fontWeight: 600, opacity: 0.8 }}>{label}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
              <button onClick={() => exportCSV('/api/export/tire-life', 'tire-life.csv')} style={{ ...btn('#2e7d32'), fontSize: 13 }}>↓ Tire Life CSV</button>
              <button onClick={() => exportCSV('/api/export/inspections', 'inspections.csv')} style={{ ...btn('#1565c0'), fontSize: 13 }}>↓ Inspections CSV</button>
            </div>
            <div style={card}>
              <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>Replacements This Month</h3>
              {dashboard.replacementsThisMonth.length === 0 ? <p style={{ color: 'var(--text2)', margin: 0 }}>No replacements this month.</p> : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>{['Vehicle','Axle','Position','Brand','Size','Vendor','Tread','Tech','Date'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {dashboard.replacementsThisMonth.map(r => (
                      <tr key={r.id}>
                        <td style={td({ fontWeight: 700 })}>{r.vehicle_name}</td>
                        <td style={td()}>{r.axle}</td>
                        <td style={td()}>{r.position}</td>
                        <td style={td()}>{r.brand}</td>
                        <td style={td()}>{r.size}</td>
                        <td style={td()}>{r.vendor}</td>
                        <td style={td()}>{r.tread_at_install ? `${r.tread_at_install}/32` : '—'}</td>
                        <td style={td()}>{r.technician}</td>
                        <td style={td({ color: 'var(--text2)', fontSize: 12 })}>{r.installed_at?.slice(0,10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    );

    if (view === 'vehicles') return (
      <div style={{ maxWidth: 980, margin: '20px auto', padding: '0 16px' }}>
        {nearestVehicles.length > 0 && (
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 11, color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: 10 }}>Nearest to You</div>
            <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
              {nearestVehicles.map(v => (
                <div key={v.id} onClick={() => selectVehicle(v)} style={{ minWidth: 130, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 12, padding: '10px 14px', cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--section2-color)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--section2-color)' }}>{v.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{v.type === 'truck' ? '🚛' : <BoxTrailerIcon size={11} />} {v.dist < 0.1 ? 'Here' : v.dist < 1 ? `${(v.dist * 5280).toFixed(0)} ft` : `${v.dist.toFixed(1)} mi`}</div>
                  {v.odometer ? <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2 }}>{v.odometer.toLocaleString()} mi</div> : null}
                </div>
              ))}
            </div>
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          <button onClick={() => { setFleetTab('truck'); setSearch(''); }} style={tabBtn(fleetTab === 'truck')}>🚛 Trucks ({trucks.length})</button>
          <button onClick={() => { setFleetTab('trailer'); setSearch(''); }} style={tabBtn(fleetTab === 'trailer')}><BoxTrailerIcon size={13} /> Trailers ({trailers.length})</button>
          <input placeholder={`Search by name or plate…`} value={search}
            onChange={e => setSearch(e.target.value)} style={{ ...inp, marginBottom: 0, flex: 1, minWidth: 180 }} />
        </div>
        <div style={{ background: 'var(--table-bg)', border: '1px solid var(--table-border)', borderRadius: 16, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Name</th>
                <th style={th}>VIN</th>
                <th style={th}>Plate</th>
                <th style={th}>{fleetTab === 'truck' ? 'Axles' : 'Config'}</th>
                <th style={th}>Odometer</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(v => (
                <tr key={v.id} onClick={() => selectVehicle(v)}
                  style={{ cursor: 'pointer', transition: 'background 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--table-hover)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={td({ color: 'var(--section2-color)', fontWeight: 700 })}>{v.name}</td>
                  <td style={td({ color: 'var(--text3)', fontSize: 12 })}>{v.vin}</td>
                  <td style={td()}>{v.license_plate}</td>
                  <td style={td({ color: (v.axle_count || v.trailer_config) ? 'var(--text2)' : 'var(--text3)' })}>
                    {fleetTab === 'truck' ? (v.axle_count ? `${v.axle_count}-axle` : '—') : (v.trailer_config || '—')}
                  </td>
                  <td style={td({ color: v.odometer ? 'var(--text)' : 'var(--text3)', fontWeight: v.odometer ? 600 : 400 })}>
                    {v.odometer ? v.odometer.toLocaleString() + ' mi' : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );

    const isTruck = selected.type === 'truck';
    const configReady = isTruck ? axleCountSet : trailerConfigSet;

    return (
      <div style={{ maxWidth: 980, margin: '20px auto', padding: '0 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 24, fontWeight: 900, color: 'var(--text)', letterSpacing: '-0.5px' }}>{isTruck ? '🚛' : <BoxTrailerIcon size={22} />} {selected.name}</h2>
        <span style={{ background: isTruck ? 'var(--stat-truck-bg)' : 'var(--stat-trailer-bg)', color: isTruck ? 'var(--stat-truck-c)' : 'var(--stat-trailer-c)', padding: '4px 12px', borderRadius: 99, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{selected.type}</span>
        {configReady && (
          <button onClick={() => isTruck ? setAxleCountSet(false) : setTrailerConfigSet(false)}
            style={{ background: 'var(--surface2)', color: 'var(--text2)', border: '1px solid var(--border)', padding: '4px 12px', borderRadius: 8, fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>Change config</button>
        )}
      </div>
      <p style={{ color: 'var(--text2)', fontSize: 13, marginBottom: 20 }}>
        {selected.vin && <span>VIN: {selected.vin} &nbsp;·&nbsp; </span>}Plate: {selected.license_plate}
        {selected.odometer ? <span> &nbsp;·&nbsp; <strong style={{ color: 'var(--section2-color)' }}>{selected.odometer.toLocaleString()} mi</strong></span> : null}
      </p>

      {isTruck && !axleCountSet && (
        <div style={{ background: 'var(--cfg-bg)', border: '1px solid var(--cfg-border)', borderRadius: 14, padding: 22, marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 14px', color: 'var(--section-color)', fontSize: 15, fontWeight: 700 }}>How many axles does this truck have?</h3>
          <div style={{ display: 'flex', gap: 10 }}>
            {[2, 3, 4].map(n => (
              <button key={n} onClick={() => saveAxleCount(n)} style={{ ...btn('#1a73e8'), fontSize: 15, padding: '12px 26px' }}>{n} Axle</button>
            ))}
          </div>
        </div>
      )}

      {!isTruck && !trailerConfigSet && (
        <div style={{ background: 'var(--cfg-bg)', border: '1px solid var(--cfg-border)', borderRadius: 14, padding: 22, marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 14px', color: 'var(--section-color)', fontSize: 15, fontWeight: 700 }}>What is this trailer's axle configuration?</h3>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {Object.keys(TRAILER_CONFIGS).map(c => (
              <button key={c} onClick={() => saveTrailerConfig(c)} style={{ ...btn('#6a1b9a'), fontSize: 13, padding: '10px 16px' }}>{c}</button>
            ))}
          </div>
        </div>
      )}



      {configReady && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginBottom: 24 }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, padding: 20, overflowX: 'auto', textAlign: 'center' }}>
            {isTruck ? (
              <TruckSVG axleCount={axleCount} driveAxleConfig={driveAxleConfig}
                selectedTires={selectedTires}
                onTireClick={handleTireClick} records={allRecords}
                onChangeAxleConfig={handleChangeAxleConfig} />
            ) : (
              <TrailerSVG trailerConfig={trailerConfig} axleConfig={driveAxleConfig}
                selectedTires={selectedTires}
                onTireClick={handleTireClick} records={allRecords}
                onChangeAxleConfig={handleChangeAxleConfig} />
            )}
            <DiagramLegend />
          </div>
          <div style={{ width: '100%' }}>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginBottom: 10, flexWrap: 'wrap' }}>
              <button
                onClick={() => { setMultiSelectMode(m => !m); setRotateMode(false); setRotateTireA(null); setSelectedTires([]); }}
                style={{ background: multiSelectMode ? 'linear-gradient(135deg,#fb923c,#c2410c)' : 'var(--surface2)', color: multiSelectMode ? '#fff' : 'var(--text2)', border: `1px solid ${multiSelectMode ? '#fb923c' : 'var(--border)'}`, borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}
              >
                {multiSelectMode ? 'Cancel Multi-Select' : 'Change Multiple Tires'}
              </button>
              <button
                onClick={() => { setRotateMode(r => !r); setMultiSelectMode(false); setRotateTireA(null); setSelectedTires([]); }}
                style={{ background: rotateMode ? 'linear-gradient(135deg,#4f8ef7,#2563eb)' : 'var(--surface2)', color: rotateMode ? '#fff' : 'var(--text2)', border: `1px solid ${rotateMode ? '#4f8ef7' : 'var(--border)'}`, borderRadius: 10, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}
              >
                {rotateMode ? 'Cancel Rotate' : 'Rotate Tires'}
              </button>
            </div>
            {multiSelectMode && selectedTires.length > 0 && (
              <button
                onClick={() => setModalOpen(true)}
                style={{ width: '100%', padding: '12px', background: 'linear-gradient(135deg,#fb923c,#c2410c)', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, fontSize: 15, cursor: 'pointer', marginBottom: 10 }}
              >
                Open Form ({selectedTires.length} {selectedTires.length === 1 ? 'tire' : 'tires'} selected)
              </button>
            )}
            {!multiSelectMode && !rotateMode && selectedTires.length === 0 && (
              <div style={{ background: 'var(--empty-bg)', border: '1px dashed var(--empty-border)', borderRadius: 12, padding: 18, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
                Tap a tire on the diagram above
              </div>
            )}
            {multiSelectMode && selectedTires.length === 0 && (
              <div style={{ background: 'var(--empty-bg)', border: '1px dashed var(--empty-border)', borderRadius: 12, padding: 18, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>
                Tap tires to select, then tap Open Form
              </div>
            )}
            {rotateMode && !rotateTireA && (
              <div style={{ background: 'rgba(79,142,247,0.08)', border: '1px dashed #4f8ef7', borderRadius: 12, padding: 18, textAlign: 'center', color: '#4f8ef7', fontSize: 13 }}>
                Tap the first tire to rotate
              </div>
            )}
            {rotateMode && rotateTireA && (
              <div style={{ background: 'rgba(79,142,247,0.08)', border: '1px dashed #4f8ef7', borderRadius: 12, padding: 18, textAlign: 'center', color: '#4f8ef7', fontSize: 13 }}>
                Axle {rotateTireA.axle} — {rotateTireA.position} selected. Tap the tire to swap with.
              </div>
            )}
          </div>
        </div>
      )}

      <TireModal
        selectedTires={selectedTires}
        isOpen={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedTires([]); setMultiSelectMode(false); setVoiceTranscript(''); setRepForm(f => ({ ...f, overrideMilesDate: false, overrideMiles: '', overrideDate: '' })); }}
        formType={formType}
        setFormType={setFormType}
        saved={saved}
        repForm={repForm}
        setRepForm={setRepForm}
        insForm={insForm}
        setInsForm={setInsForm}
        submitReplacement={submitReplacement}
        submitInspection={submitInspection}
        scanning={scanning}
        scanInputRef={scanInputRef}
        handleScan={handleScan}
        analyzingTread={analyzingTread}
        treadScanInputRef={treadScanInputRef}
        handleTreadScan={handleTreadScan}
        treadAnalysis={treadAnalysis}
        setTreadAnalysis={setTreadAnalysis}
        voiceRecording={voiceRecording}
        voiceParsing={voiceParsing}
        voiceTranscript={voiceTranscript}
        startVoice={startVoice}
        stopVoice={stopVoice}
        perTireData={perTireData}
        setPerTireData={setPerTireData}
      />

      <RotateModal
        isOpen={rotateModalOpen}
        onClose={() => { setRotateModalOpen(false); setRotateTireA(null); setRotateInstallA(null); setRotateInstallB(null); setSelectedTires([]); }}
        installA={rotateInstallA}
        installB={rotateInstallB}
        rotateForm={rotateForm}
        setRotateForm={setRotateForm}
        submitRotation={submitRotation}
        saved={rotateSaved}
      />

      <h3 style={{ marginBottom: 14, fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>History</h3>
      {history.installs.length === 0 && history.inspections.length === 0 && !(history.rotations || []).length && (
        <p style={{ color: 'var(--text3)', fontSize: 14 }}>No tire records yet for this vehicle.</p>
      )}
      {history.installs.map(r => {
        const tireId = `T-${String(r.id).padStart(4, '0')}`;
        const isOpen = !r.removed_at;
        const milesRun = r.odometer_at_removal && r.odometer_at_install ? r.odometer_at_removal - r.odometer_at_install : null;
        const isEditing = editingId === r.id;
        const inpS = { width: '100%', padding: '7px 10px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13, boxSizing: 'border-box', background: 'var(--surface-input)', color: 'var(--text)' };
        const lblS = { fontWeight: 600, fontSize: 11, display: 'block', color: 'var(--text2)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 4, marginTop: 10 };
        return (
          <div key={r.id} style={{ background: isOpen ? 'var(--hist-open-bg)' : 'var(--hist-closed-bg)', border: `1px solid ${isOpen ? 'var(--hist-open-bd)' : 'var(--hist-closed-bd)'}`, borderRadius: 12, padding: '12px 16px', marginBottom: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, fontSize: 11, background: isOpen ? 'var(--tread-bg)' : 'var(--sel-bg)', color: isOpen ? '#34d399' : 'var(--section-color)', borderRadius: 6, padding: '3px 9px', letterSpacing: '0.3px' }}>{tireId}</span>
              {isOpen && <span style={{ fontSize: 11, fontWeight: 700, color: '#34d399', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Active</span>}
              <span style={{ color: 'var(--text2)', fontSize: 13 }}>Axle {r.axle} {r.position} · {r.brand} {r.size}{r.tread_pattern ? ` ${r.tread_pattern}` : ''}{r.is_retread ? ' [Retread]' : ''}{r.vendor ? ` · ${r.vendor}` : ''}</span>
              {r.is_roadside ? <span style={{ fontSize: 11, fontWeight: 700, color: '#fb923c', background: 'var(--sel-bg)', border: '1px solid var(--sel-border)', borderRadius: 6, padding: '2px 8px' }}>Roadside</span> : null}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                <button onClick={() => { if (isEditing) { setEditingId(null); } else { setEditingId(r.id); setEditForm({ installed_at: r.installed_at?.slice(0,10) || '', odometer_at_install: r.odometer_at_install || '', tread_at_install: r.tread_at_install || '', removed_at: r.removed_at?.slice(0,10) || '', odometer_at_removal: r.odometer_at_removal || '', tread_at_removal: r.tread_at_removal || '', removal_reason: r.removal_reason || '', technician: r.technician || '', notes: r.notes || '' }); } }} style={{ background: isEditing ? 'var(--section-color)' : 'var(--surface2)', border: 'none', borderRadius: 7, padding: '4px 10px', fontSize: 12, fontWeight: 600, color: isEditing ? '#fff' : 'var(--text2)', cursor: 'pointer' }}>{isEditing ? 'Cancel' : 'Edit'}</button>
                <button onClick={() => { if (window.confirm('Delete this tire record?')) { axios.delete(`${API}/api/tire-installs/${r.id}`, {}).then(() => axios.get(`${API}/api/vehicles/${selected.id}/history`, {}).then(res => setHistory(res.data))); } }} style={{ background: 'transparent', border: 'none', borderRadius: 7, padding: '4px 8px', fontSize: 14, color: '#ef4444', cursor: 'pointer' }}>🗑</button>
              </div>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 6, lineHeight: 1.7 }}>
              <span>On: {r.tread_at_install ? `${r.tread_at_install}/32` : '—'}</span>
              {!isOpen && <span> → Off: {r.tread_at_removal ? `${r.tread_at_removal}/32` : '—'}</span>}
              {milesRun !== null && <span style={{ fontWeight: 700, color: 'var(--section2-color)' }}> · {milesRun.toLocaleString()} mi run</span>}
              {r.removal_reason && <span> · {r.removal_reason}</span>}
              {r.odometer_at_install && <span> · {r.odometer_at_install.toLocaleString()} mi at install</span>}
              <span> · {r.technician} · <span style={{ color: 'var(--text3)' }}>{r.installed_at}{r.removed_at ? ` → ${r.removed_at}` : ''}</span></span>
            </div>
            {r.photos && r.photos.split(',').filter(Boolean).map((p, i) => (
              <img key={i} src={`${API}/uploads/${p}`} alt="tire" onClick={() => setLightboxSrc(`${API}/uploads/${p}`)} style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, marginTop: 8, marginRight: 6, border: '1px solid var(--border)', cursor: 'zoom-in' }} />
            ))}
            {isEditing && (
              <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 130 }}>
                    <label style={lblS}>Install Date</label>
                    <input type="date" value={editForm.installed_at} onChange={e => setEditForm({ ...editForm, installed_at: e.target.value })} style={inpS} />
                  </div>
                  <div style={{ flex: 1, minWidth: 130 }}>
                    <label style={lblS}>Miles at Install</label>
                    <input type="number" value={editForm.odometer_at_install} onChange={e => setEditForm({ ...editForm, odometer_at_install: e.target.value })} style={inpS} placeholder="Odometer" />
                  </div>
                  <div style={{ flex: 1, minWidth: 100 }}>
                    <label style={lblS}>Tread at Install</label>
                    <input type="number" value={editForm.tread_at_install} onChange={e => setEditForm({ ...editForm, tread_at_install: e.target.value })} style={inpS} placeholder="32nds" />
                  </div>
                </div>
                {!isOpen && (
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 130 }}>
                      <label style={lblS}>Removal Date</label>
                      <input type="date" value={editForm.removed_at} onChange={e => setEditForm({ ...editForm, removed_at: e.target.value })} style={inpS} />
                    </div>
                    <div style={{ flex: 1, minWidth: 130 }}>
                      <label style={lblS}>Miles at Removal</label>
                      <input type="number" value={editForm.odometer_at_removal} onChange={e => setEditForm({ ...editForm, odometer_at_removal: e.target.value })} style={inpS} placeholder="Odometer" />
                    </div>
                    <div style={{ flex: 1, minWidth: 100 }}>
                      <label style={lblS}>Tread at Removal</label>
                      <input type="number" value={editForm.tread_at_removal} onChange={e => setEditForm({ ...editForm, tread_at_removal: e.target.value })} style={inpS} placeholder="32nds" />
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 130 }}>
                    <label style={lblS}>Technician</label>
                    <input value={editForm.technician} onChange={e => setEditForm({ ...editForm, technician: e.target.value })} style={inpS} />
                  </div>
                  <div style={{ flex: 2, minWidth: 160 }}>
                    <label style={lblS}>Notes</label>
                    <input value={editForm.notes} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} style={inpS} placeholder="Optional" />
                  </div>
                </div>
                <button onClick={() => {
                  axios.patch(`${API}/api/tire-installs/${r.id}`, editForm, {})
                    .then(() => { setEditingId(null); axios.get(`${API}/api/vehicles/${selected.id}/history`, {}).then(res => setHistory(res.data)); })
                    .catch(e => alert('Error: ' + (e.response?.data?.error || e.message)));
                }} style={{ marginTop: 12, background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 20px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>Save Changes</button>
              </div>
            )}
          </div>
        );
      })}
      {history.inspections.map(r => (
        <div key={r.id} style={{ background: 'var(--hist-ins-bg)', border: '1px solid var(--hist-ins-bd)', borderRadius: 12, padding: '12px 16px', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 11, background: 'var(--hist-ins-bg)', color: 'var(--section2-color)', borderRadius: 6, padding: '3px 9px' }}>Inspection</span>
            <span style={{ color: 'var(--text2)', fontSize: 13 }}>Axle {r.axle} {r.position} · Tread {r.tread_depth}/32 · {r.condition} · {r.technician}</span>
            <span style={{ color: 'var(--text3)', fontSize: 12, marginLeft: 'auto' }}>{r.inspected_at}</span>
            <button onClick={() => { if (window.confirm('Delete this inspection record?')) { axios.delete(`${API}/api/inspections/${r.id}`, {}).then(() => axios.get(`${API}/api/vehicles/${selected.id}/history`, {}).then(res => setHistory(res.data))); } }} style={{ background: 'transparent', border: 'none', borderRadius: 7, padding: '4px 8px', fontSize: 14, color: '#ef4444', cursor: 'pointer' }}>🗑</button>
          </div>
          {r.photos && r.photos.split(',').filter(Boolean).map((p, i) => (
            <img key={i} src={`${API}/uploads/${p}`} alt="tire" onClick={() => setLightboxSrc(`${API}/uploads/${p}`)} style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 8, marginTop: 8, marginRight: 6, border: '1px solid var(--border)', cursor: 'zoom-in' }} />
          ))}
        </div>
      ))}
      {(history.rotations || []).map(r => (
        <div key={`rot-${r.id}`} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: '12px 16px', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 700, fontSize: 11, background: 'rgba(79,142,247,0.15)', color: '#4f8ef7', borderRadius: 6, padding: '3px 9px' }}>Rotation</span>
            <span style={{ color: 'var(--text2)', fontSize: 13 }}>Axle {r.from_axle_a} {r.from_position_a} ↔ Axle {r.from_axle_b} {r.from_position_b}</span>
            <span style={{ color: 'var(--text3)', fontSize: 12, marginLeft: 'auto' }}>{r.rotated_at ? new Date(r.rotated_at).toLocaleDateString() : ''}</span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 6, lineHeight: 1.7 }}>
            {(r.tread_a || r.tread_b) && <span>{r.tread_a ? `${r.tread_a}/32` : '—'} ↔ {r.tread_b ? `${r.tread_b}/32` : '—'}</span>}
            {r.technician && <span>{(r.tread_a || r.tread_b) ? ' · ' : ''}{r.technician}</span>}
            {r.odometer ? <span> · {r.odometer.toLocaleString()} mi</span> : null}
            {r.notes ? <div style={{ marginTop: 2 }}>{r.notes}</div> : null}
          </div>
        </div>
      ))}
    </div>
  );
  }; // end renderContent

  // ── Unified layout ───────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Mobile overlay */}
      {!isDesktop && sidebarOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 20 }}
          onClick={() => setSidebarOpen(false)} />
      )}

      <Sidebar />

      {/* Main area */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'auto' }}>
        {/* Mobile top bar */}
        {!isDesktop && (
          <header style={{ position: 'sticky', top: 0, zIndex: 10, display: 'flex', alignItems: 'center', gap: 12, padding: '0 16px', height: 56, background: 'var(--bg)', borderBottom: '1px solid var(--border)', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
            <button onClick={() => setSidebarOpen(true)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text)', padding: '4px 6px', borderRadius: 8 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: 6, background: 'hsl(220,86%,44%)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="4"/></svg>
              </div>
              <span style={{ fontWeight: 800, fontSize: 17, color: 'var(--text)', letterSpacing: '-0.3px' }}>Fleet Tires</span>
            </div>
          </header>
        )}

        {renderContent()}
      </div>
      {lightboxSrc && (
        <div onClick={() => setLightboxSrc(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20, cursor: 'zoom-out' }}>
          <img src={lightboxSrc} alt="tire" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 8, boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }} />
          <button onClick={(e) => { e.stopPropagation(); setLightboxSrc(null); }} style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 36, height: 36, borderRadius: 18, fontSize: 20, lineHeight: 1, cursor: 'pointer' }}>×</button>
        </div>
      )}
    </div>
  );
}

export default App;
